/*
 * NORA PRO chemical compatibility engine.
 * One copy of the lookup and list logic, used by:
 *   - the guide page tool (theme asset assets/nora-chem-engine.js, copied by build.mjs)
 *   - the lookup tests (tools/chem-guide/test-lookups.mjs)
 *   - the assistant Worker (workers/chem-assistant), which only maps words to row ids
 * The dataset decides every answer. Nothing in here invents a rating.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.NoraChem = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var SPELL = {
    sulfuric: 'sulphuric', sulfate: 'sulphate', sulfur: 'sulphur', aluminum: 'aluminium', tire: 'tyre',
    tires: 'tyres', carburetor: 'carburettor', gasoline: 'petrol', color: 'colour', liter: 'litre',
    liters: 'litres',
  };
  var FAMILY_ORDER = ['acid', 'solvent', 'alkaline'];
  var STOP = ['the', 'for', 'and', 'of', 'to', 'my', 'in', 'is', 'are', 'can', 'does', 'with', 'which', 'what', 'seal', 'seals',
    'sprayer', 'spray', 'spraying', 'use', 'using', 'need', 'needs', 'do', 'how', 'good', 'suit', 'suits', 'work', 'works', 'will',
    'please', 'about', 'best', 'right', 'type', 'strength', 'percent', 'cent', 'per', 'on', 'it', 'its', 'an', 'at', 'be'];

  function norm(s) {
    s = String(s == null ? '' : s).toLowerCase();
    if (s.normalize) s = s.normalize('NFKD').replace(/[̀-ͯ]/g, '');
    s = s.replace(/&/g, ' and ').replace(/[^a-z0-9%.]+/g, ' ').replace(/(^|\s)\.+|\.+(\s|$)/g, ' ');
    return s.replace(/\s+/g, ' ').trim();
  }

  function spell(s) {
    return norm(s).split(' ').map(function (t) { return SPELL[t] || t; }).join(' ');
  }

  function lev(a, b) {
    if (a === b) return 0;
    var al = a.length, bl = b.length;
    if (Math.abs(al - bl) > 2) return 3;
    var prev = [], cur = [], i, j;
    for (j = 0; j <= bl; j += 1) prev[j] = j;
    for (i = 1; i <= al; i += 1) {
      cur = [i];
      for (j = 1; j <= bl; j += 1) {
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1));
      }
      prev = cur;
    }
    return prev[bl];
  }

  // Names we match against: the display name, the name without brackets, each bracket phrase, each alias.
  function labelsOf(row) {
    var out = [row.name];
    var plain = row.name.replace(/\([^)]*\)/g, ' ');
    out.push(plain);
    var m = row.name.match(/\(([^)]*)\)/g) || [];
    m.forEach(function (b) { out.push(b.replace(/[()]/g, '')); });
    (row.aka || []).forEach(function (a) { out.push(a); });
    var seen = {};
    return out.map(spell).filter(function (x) { if (!x || seen[x]) return false; seen[x] = 1; return true; });
  }

  function buildIndex(data) {
    if (data._index) return data._index;
    var idx = data.chemicals.map(function (row) {
      var labels = labelsOf(row);
      var tokens = {};
      labels.forEach(function (l) { l.split(' ').forEach(function (t) { if (t.length > 1) tokens[t] = 1; }); });
      return { row: row, labels: labels, tokens: Object.keys(tokens) };
    });
    Object.defineProperty(data, '_index', { value: idx, enumerable: false });
    return idx;
  }

  // Pull a trailing strength out of free text, for example "hydrochloric acid 10%" or "bleach 12.5 percent".
  function parseQuery(q) {
    var text = String(q == null ? '' : q);
    var strength = null;
    var m = text.match(/(\d+(?:\.\d+)?)\s*(%|per\s*cent|percent)/i);
    if (m) {
      strength = parseFloat(m[1]);
      text = text.replace(m[0], ' ');
    }
    var ph = text.match(/\bph\s*(\d+(?:\.\d+)?)/i);
    var phValue = null;
    if (ph) {
      phValue = parseFloat(ph[1]);
      text = text.replace(ph[0], ' ');
    }
    return { text: text.trim(), strength: strength, ph: phValue };
  }

  function score(entry, q, qTokens) {
    var best = 0;
    var i;
    for (i = 0; i < entry.labels.length; i += 1) {
      var l = entry.labels[i];
      if (l === q) return 100;
      var startsWith = l.indexOf(q + ' ') === 0;
      var endsWith = l.length > q.length && l.lastIndexOf(' ' + q) === l.length - q.length - 1;
      if (startsWith || endsWith) best = Math.max(best, 80);
      else if (q.length >= 3 && l.indexOf(q) > -1) best = Math.max(best, 55);
    }
    if (best >= 80) return best;
    // Every word typed must be found in the row. A partial overlap is never a match, so "sodium chlorate"
    // can not land on "sodium hypochlorite".
    var hit = 0;
    qTokens.forEach(function (t) { if (entry.tokens.indexOf(t) > -1) hit += 1; });
    if (qTokens.length && hit === qTokens.length) best = Math.max(best, 65);
    if (best < 45) {
      // Typos: one slip in each long word, nothing looser.
      var long = qTokens.filter(function (t) { return t.length >= 5; });
      var fuzzy = 0;
      long.forEach(function (t) {
        for (var k = 0; k < entry.tokens.length; k += 1) {
          if (entry.tokens[k].length >= 5 && lev(t, entry.tokens[k]) <= 1) { fuzzy += 1; break; }
        }
      });
      var short = qTokens.filter(function (t) { return t.length < 5; });
      var shortHit = short.filter(function (t) { return entry.tokens.indexOf(t) > -1; }).length;
      if (long.length && fuzzy === long.length && shortHit === short.length) best = Math.max(best, 45);
    }
    return best;
  }

  // Ranked matches for free text. Returns [{row, score}] with score >= 40.
  function search(data, query, limit) {
    var parsed = parseQuery(query);
    var q = spell(parsed.text);
    if (q.length < 2) return [];
    var qTokens = q.split(' ').filter(function (t) { return t.length > 1 && STOP.indexOf(t) === -1; });
    var idx = buildIndex(data);
    var out = [];
    idx.forEach(function (entry) {
      var s = score(entry, q, qTokens.length ? qTokens : [q]);
      if (s >= 40) out.push({ row: entry.row, score: s });
    });
    out.sort(function (a, b) { return b.score - a.score || a.row.name.length - b.row.name.length; });
    return out.slice(0, limit || 8);
  }

  // Matches (score 65 and up) are safe to show as the answer. Suggestions (45 to 64) are typo guesses
  // and must be offered as "did you mean", never shown as an answer.
  function lookup(data, query, limit) {
    var all = search(data, query, limit || 8);
    return {
      parsed: parseQuery(query),
      matches: all.filter(function (x) { return x.score >= 65; }),
      suggestions: all.filter(function (x) { return x.score < 65; }),
    };
  }

  // True when the top match is clearly the one the user meant (no close runner up).
  function isClear(result) {
    var m = result.matches;
    if (!m.length) return false;
    return m.length === 1 || m[0].score - m[1].score >= 15;
  }

  function byId(data, id) {
    for (var i = 0; i < data.chemicals.length; i += 1) if (data.chemicals[i].id === id) return data.chemicals[i];
    return null;
  }

  // The answer for one row. opts: { strength: number|null, hot: boolean }
  function resolve(data, row, opts) {
    opts = opts || {};
    var res = {
      id: row.id, name: row.name, group: row.group,
      rating: row.rating, fams: row.rating === 'ask' ? [] : (row.fams || []).slice(),
      seal: row.seal, sealGroup: row.sealGroup, spec: row.spec || null, text: null,
      max: row.max || null, col: row.col, note: row.note || null,
      needsStrength: false, scale: row.scale || null, hot: false, strength: null,
    };
    if (row.bands && row.bands.length) {
      var s = typeof opts.strength === 'number' && !isNaN(opts.strength) ? opts.strength : null;
      if (s === null) {
        // A cap row is rated as it stands; its single band only matters above the top strength.
        res.needsStrength = !row.cap;
      } else {
        res.strength = s;
        var band = null;
        for (var i = 0; i < row.bands.length; i += 1) {
          if (s <= row.bands[i].upto) { band = row.bands[i]; break; }
        }
        if (band) {
          res.rating = band.rating;
          res.fams = band.rating === 'ask' ? [] : (band.fams || []).slice();
          res.spec = band.spec || null;
          res.text = band.text;
        } else {
          res.rating = 'ask';
          res.fams = [];
          res.text = row.over || 'No source rates a sprayer at this strength. Ask us.';
        }
      }
    }
    if (opts.hot) {
      res.hot = true;
      res.rating = 'ask';
      res.fams = [];
      res.text = data.meta.temperature_note;
    }
    res.famLabels = res.fams.map(function (f) { return data.families[f].label; });
    return res;
  }

  function subsets(arr) {
    var out = [];
    for (var mask = 1; mask < (1 << arr.length); mask += 1) {
      var s = [];
      for (var i = 0; i < arr.length; i += 1) if (mask & (1 << i)) s.push(arr[i]);
      out.push(s);
    }
    out.sort(function (a, b) { return a.length - b.length; });
    return out;
  }

  // Work out how many sprayers a chemical list needs. items: [{row, strength, hot}]
  function planList(data, items) {
    var resolved = items.map(function (it) {
      var r = resolve(data, it.row, { strength: it.strength, hot: it.hot });
      r.input = it;
      return r;
    });
    var asks = resolved.filter(function (r) { return !r.fams.length; });
    var covered = resolved.filter(function (r) { return r.fams.length; });
    var units = [];
    if (covered.length) {
      var best = null;
      subsets(FAMILY_ORDER).forEach(function (set) {
        if (best && set.length > best.length) return;
        var ok = covered.every(function (r) { return r.fams.some(function (f) { return set.indexOf(f) > -1; }); });
        if (!ok) return;
        if (!best) { best = set; return; }
        var groups = function (x) { var g = {}; x.forEach(function (f) { g[data.families[f].sealGroup] = 1; }); return Object.keys(g).length; };
        if (set.length === best.length && groups(set) < groups(best)) best = set;
      });
      units = best || [];
    }
    covered.forEach(function (r) {
      var pick = r.fams.filter(function (f) { return units.indexOf(f) > -1; });
      r.assigned = pick.length ? pick[0] : r.fams[0];
    });
    var sealGroups = {};
    units.forEach(function (f) { sealGroups[data.families[f].sealGroup] = 1; });
    var sealList = Object.keys(sealGroups);
    var mixesSeals = sealList.length > 1;
    var conditional = resolved.filter(function (r) { return r.rating === 'conditional' || r.needsStrength; });
    var messages = [];
    var summary = '';
    if (!resolved.length) messages.push('Add the chemicals you spray.');
    if (units.length === 1) summary = 'One sprayer covers the chemicals we can match: the ' + data.families[units[0]].label.toLowerCase() + '.';
    if (units.length > 1) summary = 'You need ' + units.length + ' sprayers: ' + units.map(function (f) { return 'the ' + data.families[f].label.toLowerCase(); }).join(', ') + '.';
    if (mixesSeals) messages.push('This list mixes VITON and EPDM chemicals, so the seals cannot be shared. Keep each chemical in its own sprayer and rinse between uses.');
    else if (units.length > 1) messages.push('Both sprayers use VITON seals, but the acid and solvent models are different builds. A VITON acid sprayer is not for petroleum solvents, and the reverse.');
    if (asks.length) messages.push(asks.length + (asks.length === 1 ? ' chemical needs' : ' chemicals need') + ' a check with us before you buy: ' + asks.map(function (r) { return r.name; }).join(', ') + '.');
    if (conditional.length) messages.push('Some answers are conditional or need a strength. Check the notes below each one.');
    return {
      items: resolved, asks: asks, covered: covered, units: units,
      unitLabels: units.map(function (f) { return data.families[f].label; }),
      sealGroups: sealList, mixesSeals: mixesSeals, messages: messages, summary: summary,
      oneSprayer: units.length === 1 && !asks.length,
    };
  }

  // Share link payload: "hydrochloric-acid:10,xylene,bleach". Temperature goes in a separate flag.
  function encodeList(entries) {
    return entries.map(function (e) { return e.row.id + (typeof e.strength === 'number' && !isNaN(e.strength) ? ':' + e.strength : ''); }).join(',');
  }
  function decodeList(data, str) {
    var out = [];
    String(str || '').split(',').slice(0, 20).forEach(function (part) {
      var bits = part.split(':');
      var row = byId(data, bits[0].replace(/[^a-z0-9-]/g, ''));
      if (!row) return;
      var s = bits[1] === undefined ? null : parseFloat(bits[1]);
      out.push({ row: row, strength: s === null || isNaN(s) ? null : s });
    });
    return out;
  }

  // Quote form prefill. The quote page reads chemical, seal and note.
  function quoteParams(data, plan, hot) {
    var chem = plan.items.map(function (r) {
      return r.name + (r.strength !== null && r.strength !== undefined ? ' (' + r.strength + (r.scale === 'pH' ? ' pH' : '%') + ')' : '');
    }).join('; ');
    var lines = plan.items.map(function (r) {
      var what = r.fams.length ? data.families[r.assigned || r.fams[0]].label : 'ask us' + (r.spec ? ', ' + r.spec : '');
      return r.name + (r.strength !== null && r.strength !== undefined ? ' ' + r.strength + (r.scale === 'pH' ? ' pH' : '%') : '') + ': ' + what;
    });
    var note = 'From the NORA PRO guide (dataset v' + data.meta.version + '). ' + lines.join('. ') + '.' + (hot ? ' The liquid is warmer than 30 °C.' : '');
    return { chemical: chem, seal: plan.sealGroups.join(' and '), note: note };
  }

  // The models we stock for one or more families, grouped. Klager models are rated on the chart. Other
  // brands share the seal but are not Klager's build. Empty when the dataset has no models.
  function modelsFor(data, fams) {
    var list = data.models || [];
    return (fams || []).filter(function (f, i, a) { return a.indexOf(f) === i && data.families[f]; }).map(function (f) {
      var inFam = list.filter(function (m) { return m.fam === f; });
      return {
        fam: f,
        label: data.families[f].label,
        rated: inFam.filter(function (m) { return m.brand === 'Klager'; }),
        other: inFam.filter(function (m) { return m.brand !== 'Klager'; }),
      };
    }).filter(function (g) { return g.rated.length || g.other.length; });
  }

  return {
    norm: norm, spell: spell, parseQuery: parseQuery, search: search, lookup: lookup, isClear: isClear, byId: byId, resolve: resolve,
    planList: planList, encodeList: encodeList, decodeList: decodeList, quoteParams: quoteParams, modelsFor: modelsFor,
    FAMILY_ORDER: FAMILY_ORDER,
  };
});

/*
 * NORA PRO Sprayer Troubleshooter: the data (NoraFixData). Read by fix-engine.js.
 * Rules for editing this file:
 * - Every advice line names its source ids (src). SOURCES.md has each source with the date read and its exact words.
 *   test-engine.mjs fails on a line with no source, or a source id that is not in SOURCES below and in SOURCES.md.
 * - Australian English, plain words, short sentences. No dashes, no prices, metric only (mm, cm, L, bar, kPa).
 * - Chemistry is never decided here: the seal finder uses the Chemical Compatibility Guide dataset.
 * - A part is never said to fit a model here: the live product page decides (fix-engine.js, readProduct).
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.NoraFixData = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

// Sources. Ids are used by the advice lines below; SOURCES.md has each one's exact words and the date read.
  var SOURCES = {
    'NP-RG': { short: 'NORA PRO Refill and Maintenance Guide', title: 'NORA PRO, How to Refill and Maintain a Pressure Sprayer (norapro.com.au/pages/refill-and-repair-guide)', read: '2026-10-19' },
    'SO-SEALS': { short: 'Solo: pressure sprayer seals', title: 'Solo Inc., Pressure Sprayer Seals: What You Need to Know (updated 19 May 2026)', read: '2026-10-19' },
    'SO-BPTS': { short: 'Solo: backpack sprayer troubleshooting', title: 'Solo Inc., Troubleshooting Performance Issues with your Solo Backpack Sprayer (updated 8 Sep 2022)', read: '2026-10-19' },
    'SO-HHTS': { short: 'Solo: handheld sprayer troubleshooting guide', title: 'Solo Product Support, Troubleshooting Guide (handheld sprayers)', read: '2026-10-19' },
    'SO-REPAIR': { short: 'Solo: sprayer repair', title: 'Solo Product Support, Sprayer Repair (handheld sprayers)', read: '2026-10-19' },
    'SO-VALVE': { short: 'Solo: shut off valve repair', title: 'Solo Product Support, Shut-Off Valve Repair', read: '2026-10-19' },
    'SO-MAINT': { short: 'Solo: handheld sprayer maintenance', title: 'Solo Product Support, Maintenance (handheld sprayers)', read: '2026-10-19' },
    'SO-BPMAINT': { short: 'Solo: backpack maintenance and storage', title: 'Solo Product Support, Maintenance and Storage Tips (backpack sprayers)', read: '2026-10-19' },
    'SO-BPSPRAY': { short: 'Solo: backpack spraying', title: 'Solo Product Support, Spraying (backpack sprayers)', read: '2026-10-19' },
    'SO-425': { short: 'Solo 425 and 475 backpack manual', title: 'Solo Operator’s Manual and Parts List 425, 475 and others (US9425739, 01-22)', read: '2026-10-19' },
    'SO-456': { short: 'Solo 454, 456 and 458 manual', title: 'Solo Operator’s Manual and Parts List, Compression Sprayers 454, 456, 458 (US9456702, 09-19)', read: '2026-10-19' },
    'SO-201': { short: 'Solo 201 and 202 manual', title: 'Solo 201 and 202 Pressure Sprayer, Operator’s manual (12/2022)', read: '2026-10-19' },
    'SO-301': { short: 'Solo 301 and 302 CLEANLine manual', title: 'SOLO 301 A, 302 A, 301 B, 302 B Pressure Sprayer, Instruction manual (01/2018)', read: '2026-10-19' },
    'SO-303F': { short: 'Solo 303 F foam sprayer manual', title: 'SOLO 303 FA and 303 FB Foam sprayer, Original instructions (2022)', read: '2026-10-19' },
    'SO-305': { short: 'Solo 305 and 307 CLEANLine manual', title: 'SOLO CLEANLine 305 A, 307 A, 305 B, 307 B Pressure Sprayer, Original instructions (2020)', read: '2026-10-19' },
    'GL-PRIMA8': { short: 'GLORIA prima 8 manual', title: 'GLORIA prima 8 pressure sprayer, user manual (980042-00, 2017)', read: '2026-10-19' },
    'GL-405T': { short: 'GLORIA 405 T to 510 T manual', title: 'GLORIA high performance sprayers 405 T, 505 T, 410 T, 510 T, user manual (979066-02, 2024)', read: '2026-10-19' },
    'GL-KNAP': { short: 'GLORIA piston knapsack sprayer manual', title: 'GLORIA piston knapsack sprayers Hobby 1200 and 1800, Pro 1300 and 1800, user manual (977650-03, 2024)', read: '2026-10-19' },
    'GL-FM10': { short: 'GLORIA FoamMaster FM 10 instructions', title: 'GLORIA FoamMaster FM 10, operating instructions (977091-04, 2024)', read: '2026-10-19' },
    'GL-FM30': { short: 'GLORIA FoamMaster FM 30 manual', title: 'GLORIA FoamMaster FM 30, user manual (2018)', read: '2026-10-19' },
    'BI-FM125': { short: 'Birchmeier Foam-Matic 1.25 manual', title: 'Birchmeier Foam-Matic 1.25 P and E, operating manual (11904201)', read: '2026-10-19' },
    'BI-GS': { short: 'Birchmeier Garden Star manual', title: 'Birchmeier Garden Star 3 and 5, operating manual (11859211, v1124)', read: '2026-10-19' },
    'BI-FLOX': { short: 'Birchmeier Flox 10 and Iris 15 manual', title: 'Birchmeier Flox 10 and Iris 15 backpack sprayers, operating manual (12002802, v0424)', read: '2026-10-19' },
    'BI-RPD': { short: 'Birchmeier RPD 15 PB1 manual', title: 'Birchmeier RPD 15 PB1 backpack sprayer, operating manual (v0324)', read: '2026-10-19' },
    'BI-SAFETY': { short: 'Birchmeier general safety rules', title: 'Birchmeier general safety regulations leaflet (11947901, v0221)', read: '2026-10-19' },
    'BI-FAQ': { short: 'Birchmeier FAQ', title: 'Birchmeier FAQ page (birchmeier.com)', read: '2026-10-19' },
    'BI-TIPS': { short: 'Birchmeier tips and tricks', title: 'Birchmeier Tips and tricks page (birchmeier.com)', read: '2026-10-19' },
    'ME-TS': { short: 'Mesto FAQ: troubleshooting', title: 'MESTO FAQ, Troubleshooting (mesto.de)', read: '2026-10-19' },
    'ME-CLEAN': { short: 'Mesto FAQ: device cleaning', title: 'MESTO FAQ, Device Cleaning (mesto.de)', read: '2026-10-19' },
    'ME-CHART': { short: 'Mesto resistance chart', title: 'MESTO Resistance Chart (English, 2023)', read: '2026-10-19' },
    'ME-3132': { short: 'Mesto 1.0 and 1.5 L CLEANER instructions', title: 'MESTO pressure sprayers 3132, 1.0 and 1.5 L (CLEANER range), operating instructions (2293405)', read: '2026-10-19' },
    'ME-CLEANER': { short: 'Mesto CLEANER 5 and 10 L instructions', title: 'MESTO CLEANER and PROFI PLUS 3275 and 3270, operating instructions (6993140)', read: '2026-10-19' },
    'ME-FOAMER': { short: 'Mesto FOAMER instructions', title: 'MESTO FOAMER 3232FO and 3232FE, operating instructions (6693181)', read: '2026-10-19' },
    'ME-KNAP': { short: 'Mesto backpack sprayer instructions', title: 'MESTO backpack sprayers 3558P, RS125, RS185, operating instructions (7393407)', read: '2026-10-19' },
    'KL-OP': { short: 'Klager Spray Line instructions', title: 'Klager, Operating instructions for Compressed Air Sprayers, Spray Line acid, solve, clean and NBR versions (last updated 02/2026)', read: '2026-10-19' },
    'KL-MINI': { short: 'Klager mini sprayer instructions', title: 'Klager, Operating instructions for Compressed Air Sprayers, mini sprayers (last updated 08/2021)', read: '2026-10-19' },
    'KL-FOAM': { short: 'Klager Foamya foam sprayer instructions', title: 'Klager, FOAMYA compressed air sprayer, operating instructions (2026)', read: '2026-10-19' },
    'KL-MPOP': { short: 'Klager Masterpiece Line instructions', title: 'Klager, Masterpiece Line compressed air sprayers Solve+, Acid+ and Clean+, operating instructions (02/2026)', read: '2026-10-19' },
    'KL-CARE': { short: 'Klager: cleaning and care tips', title: 'Klager, Professional tips: the correct cleaning and care of your Kläger Plastik compressed air sprayer (klaeger-spraying.com)', read: '2026-10-19' },
    'KL-REFILL': { short: 'Klager: care, maintenance and spare parts', title: 'Klager, The refill concept by Kläger (klaeger-spraying.com)', read: '2026-10-19' },
    'KL-RES': { short: 'Klager resistance list', title: 'Klager resistance list 4.009 (02/2026): notes on resistance and sprayer life', read: '2026-10-19' },
    'BR-APPL': { short: 'Bravo: chemical applicator notes', title: 'Bravo Company, Chemical Applicator: important notes and instructions (insert for pump up sprayers, 2025)', read: '2026-10-19' },
    'BR-HOWTO': { short: 'Bravo: how to replace the GLORIA piston O ring', title: 'Bravo Company, How to Replace GLORIA Poly Sprayers Piston O-Ring, with the steel rod plate version (2018 to 2024)', read: '2026-10-19' },
    'BR-STOP': { short: 'Bravo: how to unblock a sprayer nozzle', title: 'Bravo Company, STOP! How to Unblock Sprayer Nozzle (sticker)', read: '2026-10-19' },
    'BR-AR5': { short: 'Bravo 5 L acid sprayer manual', title: 'Bravo Company, instruction manual for the GLORIA 5 L acid resistant sprayer (AR5)', read: '2026-10-19' },
    'BR-SOX': { short: 'Bravo AR5 and SOX 5 data sheet', title: 'Bravo Company, Technical Data Sheet AR5 SOX (Bravo’s English edit of the GLORIA SOX 5 manual)', read: '2026-10-19' },
    'BR-FOAM': { short: 'Bravo 1.2 L foam sprayer instructions', title: 'Bravo Company, instructions for the 1.2 Litre Industrial Mini Foam Sprayer (Washfoam 1.2)', read: '2026-10-19' },
    'BR-SURE': { short: 'Bravo Sure Shot instructions', title: 'Bravo Company, Sure Shot refillable aerosol sprayer instructions (2016)', read: '2026-10-19' },
    'NP-WARR': { short: 'NORA PRO Warranty Information', title: 'NORA PRO, Warranty Information (norapro.com.au/pages/warranty-information)', read: '2026-10-19' },
  };

  var TYPES = [
    { id: 'trigger', name: 'Trigger spray bottle', hint: 'A hand held bottle with a trigger head' },
    { id: 'pressure', name: 'Pump up pressure sprayer', hint: 'A bottle or tank you pump up, from 1 to 10\u00a0L' },
    { id: 'knapsack', name: 'Knapsack sprayer', hint: 'A backpack sprayer you pump as you go' },
    { id: 'foamer', name: 'Pump up foam sprayer', hint: 'A pump up sprayer with a foam nozzle' },
  ];

  var P3 = ['pressure', 'knapsack', 'foamer'];
  var PF = ['pressure', 'foamer'];

  // The problems, in the order shown. names: the wording for one sprayer type. notes: shown under the causes.
  var SYMPTOMS = [
    { id: 'leak-trigger', name: 'Leaks or drips at the trigger valve or nozzle', names: { trigger: 'Leaks at the trigger or where it screws on' } },
    { id: 'leak-pump', name: 'Leaks at the pump or the top of the tank' },
    { id: 'leak-hose', name: 'Leaks at the hose or lance' },
    { id: 'no-pressure', name: 'Will not build pressure', names: { trigger: 'Will not spray, or only dribbles' } },
    { id: 'loses-pressure', name: 'Builds pressure, then loses it', names: { knapsack: 'Pressure drops fast as you spray' },
      notes: [{ text: 'A test from Mesto’s manual, for a sprayer with a pressure gauge: pump the empty sprayer to 2 bar. It should lose no more than 0.5 bar in 30 minutes.', src: ['ME-CLEANER'], types: PF }] },
    { id: 'uneven', name: 'Sprays unevenly, drips or clogs', names: { foamer: 'Foams poorly, sprays unevenly or clogs' },
      notes: [{ text: 'A little squirting and spitting when you start to foam is normal.', src: ['BR-FOAM'], types: ['foamer'] }] },
    { id: 'seals', name: 'Seals swollen, soft or cracked',
      notes: [{ text: 'Seals wear out even with the right chemical. With the harshest chemicals, Klager counts 3 to 6 months of life as a very good result.', src: ['KL-RES'], types: P3 }] },
    { id: 'hose-collapsed', name: 'Hose collapsed, flat or damaged', names: { knapsack: 'Hose or tank sucked in (collapsed)' },
      notes: [{ text: 'If a hose goes soft or flat with one chemical, send us the SDS and the sprayer model and we will check it.', src: ['NP-RG'] }] },
    { id: 'pump-hard', name: 'Pump hard to push', names: { knapsack: 'Pump lever stiff, or it moves on its own' } },
  ];

  // Before any repair. Shown first, every time.
  var SAFETY = [
    { text: 'Release the pressure before you open it or undo any part: pull the relief valve, upright and pointed away from your face. With no valve to pull, as on a Klager, unscrew the head slowly to let the air out.', src: ['NP-RG', 'SO-201', 'GL-405T', 'BI-SAFETY', 'ME-CLEANER', 'KL-OP', 'KL-MPOP', 'BR-APPL', 'BR-SOX'], types: PF },
    { text: 'Release the pressure in the hose and lance first: open the trigger valve and spray into a safe container.', src: ['SO-425', 'GL-KNAP'], types: ['knapsack'] },
    { text: 'Rinse it with clean water before a repair, spraying the water into a safe container, unless the SDS says water is wrong for that chemical.', src: ['SO-BPTS', 'NP-RG', 'BR-SOX'] },
    { text: 'Wear the gloves, eye protection and clothing the SDS lists, in fresh air.', src: ['SO-BPTS', 'NP-RG', 'BI-FAQ'] },
    { text: 'Never blow through a nozzle or valve with your mouth. After a repair, test it with clean water, pointed away from people and animals, and if it still leaks, do not use it.', src: ['GL-PRIMA8', 'ME-3132', 'BI-FLOX', 'SO-BPTS', 'KL-OP', 'BR-APPL'] },
    { text: 'Use genuine parts and do not alter the sprayer, or our warranty will not cover it. If a new one seems faulty, ask us before you repair it.', src: ['NP-WARR', 'KL-OP', 'BR-SOX'] },
    { text: 'Never use a sprayer that has held weed killer or other pesticides for anything else: even slight residues can harm plants. The label and the SDS come first.', src: ['BI-TIPS', 'GL-KNAP', 'ME-CHART', 'NP-RG'] },
  ];

  // Sections of the Refill and Maintenance Guide a cause links to (ids added to the guide's headings at go live).
  var GUIDE = {
    refill: { id: 'how-to-refill', label: 'How to refill, in our Refill and Maintenance Guide' },
    care: { id: 'care-and-maintenance', label: 'Care after the job, in our Refill and Maintenance Guide' },
    repair: { id: 'repair-do-not-replace', label: 'Repair, do not replace, in our Refill and Maintenance Guide' },
  };

  // The causes. Each line: text, src (source ids), and types when it is only for some sprayers.
  var CAUSES = [
    { id: 'cap-loose', title: 'The head or cap is not screwed on tight', kind: 'use', guide: 'refill', part: null,
      why: [{ text: 'A tank that is not sealed cannot build or hold pressure.', src: ['ME-CLEANER', 'SO-HHTS', 'KL-OP'] }],
      checks: [
        { text: 'Check the head sits square on the tank and is screwed on hand tight.', src: ['NP-RG'] },
        { text: 'Check the cap seal is in place, flat and not twisted.', src: ['SO-HHTS', 'SO-REPAIR'] },
      ],
      fix: [
        { text: 'Release the pressure, start the thread square and tighten the head by hand.', src: ['NP-RG', 'ME-CLEANER'] },
        { text: 'If the head is hard to screw on, a little silicone grease on the cap seal and the tank rim helps.', src: ['SO-301', 'SO-305'] },
      ] },
    { id: 'no-air-buffer', title: 'It is filled too full, so there is no air space', kind: 'use', guide: 'refill', part: null,
      why: [{ text: 'The air above the liquid is what holds the pressure. A bottle filled to the brim will not hold a charge.', src: ['NP-RG'] }],
      checks: [
        { text: 'Check the liquid is no higher than the fill mark on the bottle.', src: ['NP-RG', 'KL-OP', 'BR-AR5'] },
        { text: 'A Klager Foamya needs a bigger air space: fill it only about half way.', src: ['KL-FOAM'], types: ['foamer'] },
      ],
      fix: [{ text: 'Release the pressure, pour some out to the fill mark, refit the head and pump again.', src: ['NP-RG'] }] },
    { id: 'piston-oring', title: 'The pump piston O ring is dry or worn', titles: { knapsack: 'The pump piston, collar or diaphragm is worn or dry' }, kind: 'seal-kit', guide: 'care', part: 'kit',
      why: [
        { text: 'This O ring seals the pump as you push it. It does the work and it dries out first.', src: ['SO-456', 'NP-RG', 'KL-CARE'], types: PF },
        { text: 'A worn piston O ring is the usual reason a sprayer will not build or hold pressure.', src: ['NP-RG', 'GL-405T', 'ME-TS'], types: PF },
        { text: 'The piston and collar, or the diaphragm, seal the pump. Worn, they give little resistance and no pressure; dry, they make the lever stiff.', src: ['SO-BPTS', 'ME-KNAP', 'BI-RPD'], types: ['knapsack'] },
      ],
      checks: [
        { text: 'Release the pressure, take the pump out and look at the O ring on the piston: smooth, whole and lightly greased.', src: ['SO-MAINT', 'SO-456'], types: PF },
        { text: 'Check the piston and collar, or the diaphragm, for wear and damage.', src: ['SO-BPTS', 'ME-KNAP', 'BI-RPD'], types: ['knapsack'] },
      ],
      fix: [
        { text: 'Grease the O ring with the lubricant your sprayer’s manual names. On EPDM seals use silicone oil only: petroleum based grease swells EPDM.', src: ['NP-RG', 'GL-405T', 'SO-305', 'KL-REFILL', 'KL-CARE'], types: PF },
        { text: 'Grease the collar or diaphragm with the lubricant your sprayer’s manual names. On EPDM seals use silicone oil only: petroleum based grease swells EPDM.', src: ['SO-BPTS', 'SO-425', 'ME-KNAP', 'NP-RG'], types: ['knapsack'] },
        { text: 'If it is worn, cracked or swollen, fit a new O ring or the sprayer’s seal kit, matched to your chemical.', src: ['GL-405T', 'ME-TS', 'NP-RG', 'KL-OP'], types: PF },
        { text: 'On a GLORIA poly sprayer: pull the pump handle right up and turn it left past the click until the collar comes free. Fit the new O ring and grease it. Check both collar pieces sit right, hold them down, pull the handle up and turn it right to lock.', src: ['BR-HOWTO'], types: ['pressure'] },
        { text: 'A GLORIA steel rod pump has a piston plate instead of the O ring. It comes out the same way.', src: ['BR-HOWTO'], types: ['pressure'] },
        { text: 'Before you put it back in the tank, pump it in the air: if it sounds and feels right, it is together properly.', src: ['BR-HOWTO'], types: ['pressure'] },
        { text: 'Replace a worn collar, piston or diaphragm.', src: ['SO-BPTS', 'ME-KNAP', 'BI-RPD'], types: ['knapsack'] },
      ] },
    { id: 'relief-valve', title: 'The pressure relief valve is dirty or its O ring is worn', kind: 'clean', guide: 'refill', part: 'valve',
      why: [{ text: 'The valve lets pressure out on purpose. Grit, or a worn O ring, lets it out all the time.', src: ['NP-RG', 'SO-HHTS'] }],
      checks: [{ text: 'If it has a relief valve you can pull, pull it once and let it go: it should spring back to where it started.', src: ['GL-FM10', 'BR-AR5'] }],
      fix: [
        { text: 'Clean the valve and grease its O ring. If it still leaks, replace the O ring or the valve.', src: ['SO-HHTS', 'SO-REPAIR', 'BI-GS'] },
        { text: 'On a Klager the relief valve is inside the pump. If one will not hold pressure and its seals are good, ask us which part it needs.', src: ['KL-MPOP', 'KL-OP'] },
        { text: 'Never use a sprayer whose relief valve does not work properly.', src: ['SO-201'] },
      ] },
    { id: 'cap-seal', title: 'The seal under the head is worn, twisted or missing', kind: 'seal-kit', guide: null, part: 'kit',
      why: [{ text: 'A worn, twisted or missing seal under the head lets the sprayer leak and lose pressure.', src: ['SO-REPAIR', 'SO-HHTS', 'NP-RG'] }],
      checks: [
        { text: 'Release the pressure, take the head off and look at the seal: flat, whole and sitting right.', src: ['SO-REPAIR', 'ME-KNAP'] },
        { text: 'Check the rim of the tank too: a damaged rim will not seal.', src: ['SO-HHTS'] },
      ],
      fix: [
        { text: 'Seat the seal straight and grease it lightly. Replace it if it is damaged.', src: ['SO-HHTS', 'BI-GS'] },
        { text: 'If the tank rim is damaged, replace the tank. Never repair a tank.', src: ['SO-HHTS', 'BR-SOX'] },
      ] },
    { id: 'valve-dirty', title: 'A valve in the pump is dirty, worn or out of place', titles: { knapsack: 'A pump valve is dirty or worn' }, kind: 'clean', guide: null, part: 'pump',
      why: [
        { text: 'A small valve at the bottom of the pump keeps liquid out of it. If the valve is dirty, worn or out of place, the pump gives no resistance and builds no pressure.', src: ['SO-456', 'SO-REPAIR'], types: PF },
        { text: 'Dirt that stops a valve disc closing, or a worn valve disc, makes the lever move too easily, or pulls it up or down.', src: ['ME-KNAP', 'BI-FLOX'], types: ['knapsack'] },
      ],
      checks: [
        { text: 'If the pump gives no resistance as you push it, check the valve at the bottom of the pump is in place, clean and sealing.', src: ['SO-REPAIR'], types: PF },
        { text: 'Liquid in the pump cylinder, or coming out of the pump, means a valve is letting it past.', src: ['SO-456', 'ME-CLEANER'], types: PF },
        { text: 'If the lever moves very easily, or is pulled up or down on its own, check the intake and pressure valves for dirt.', src: ['ME-KNAP', 'BI-FLOX'], types: ['knapsack'] },
      ],
      fix: [
        { text: 'Clean the valve. Replace it if it is worn or damaged.', src: ['SO-BPTS', 'ME-CLEANER', 'BI-FLOX', 'ME-KNAP'] },
      ] },
    { id: 'pump-leak', title: 'A pump seal, O ring or diaphragm is worn or damaged', kind: 'seal-kit', guide: null, part: 'kit',
      why: [{ text: 'Leaks at the pump come from a worn or damaged collar, piston, O ring, gasket or diaphragm.', src: ['SO-BPTS', 'BI-RPD'] }],
      checks: [{ text: 'Release the pressure and find where it leaks: inside or outside the pump cylinder, or at the diaphragm housing.', src: ['SO-BPTS'] }],
      fix: [
        { text: 'Tighten the pump’s union nut. If it still leaks, replace the worn O ring, collar, gasket or diaphragm.', src: ['SO-BPTS', 'BI-FLOX', 'BI-RPD'] },
      ] },
    { id: 'air-chamber-full', title: 'The pressure chamber is full of liquid', kind: 'use', guide: null, part: null,
      why: [{ text: 'The chamber needs a cushion of air. Storing the sprayer under pressure fills it with liquid, so it sprays only while you pump, or the pump goes hard after a few strokes.', src: ['SO-BPTS', 'ME-KNAP'] }],
      checks: [{ text: 'Does it spray only while you pump, or does the pressure last only a moment?', src: ['SO-BPTS', 'ME-KNAP'] }],
      fix: [
        { text: 'Spray or drain the tank completely empty, then pump with no liquid in it to fill the chamber with air again.', src: ['SO-BPSPRAY', 'ME-KNAP'] },
        { text: 'From now on, release the pressure after each use and pump it completely empty at least every fifth fill.', src: ['SO-BPTS', 'ME-KNAP'] },
      ] },
    { id: 'nozzle-blocked', title: 'The nozzle is blocked, worn or missing its insert', kind: 'clean', guide: 'repair', part: 'nozzle',
      why: [
        { text: 'An uneven spray usually comes from a blocked nozzle. Nozzles also wear with use.', src: ['SO-425', 'GL-405T', 'BR-APPL'] },
        { text: 'Some chemicals form crystals that block the nozzle.', src: ['KL-CARE'] },
      ],
      checks: [
        { text: 'Take the nozzle off and look for dirt or dried chemical.', src: ['ME-TS', 'SO-301'] },
        { text: 'Check the swirl insert is in the nozzle. Without it you get a jet, not a spray.', src: ['ME-TS', 'ME-KNAP'], types: P3 },
      ],
      fix: [
        { text: 'Unscrew the nozzle cap and clean the nozzle with clean water and a soft brush, or with compressed air. Never use a hard object, never tap it on a hard surface, and never blow through it with your mouth.', src: ['SO-301', 'BI-FM125', 'GL-PRIMA8', 'KL-OP', 'KL-CARE', 'BR-STOP', 'BR-APPL'] },
        { text: 'If the spray is still uneven, replace the nozzle.', src: ['SO-301', 'BI-GS'] },
        { text: 'Next time, set the pump head down on a clean surface while you refill.', src: ['BR-APPL', 'BR-STOP'], types: PF },
      ] },
    { id: 'filter-blocked', title: 'A filter is blocked', kind: 'clean', guide: null, part: null,
      why: [
        { text: 'A blocked filter gives a poor spray pattern, or no spray even with pressure.', src: ['GL-PRIMA8', 'ME-CLEANER'] },
        { text: 'Some chemicals form crystals that block the strainer on the suction tube.', src: ['KL-CARE'], types: PF },
      ],
      checks: [
        { text: 'Check the filter in the trigger valve, and the one on the suction tube inside the tank.', src: ['GL-PRIMA8', 'ME-CLEANER', 'BI-GS', 'BR-STOP', 'KL-OP'], types: PF },
        { text: 'Check the filter in the trigger valve, and the inlet screen at the base of the pressure chamber.', src: ['BI-FLOX', 'SO-BPMAINT'], types: ['knapsack'] },
      ],
      fix: [
        { text: 'Rinse the filters under running water, brush them clean, or clear them with compressed air.', src: ['ME-CLEANER', 'SO-BPMAINT', 'KL-OP', 'BR-APPL'] },
        { text: 'Next time, pour the mix in through the filling strainer.', src: ['GL-405T', 'BI-FAQ'] },
      ] },
    { id: 'air-in-line', title: 'There is air in the hose and lance', kind: 'use', guide: null, part: null,
      why: [{ text: 'Air trapped in the hose makes the nozzle drip or spit when you start.', src: ['ME-TS', 'ME-CLEANER', 'KL-MINI'] }],
      checks: [{ text: 'Does it drip or spit at first, then settle down?', src: ['ME-TS', 'ME-CLEANER'] }],
      fix: [{ text: 'Point the lance up at an angle, away from you and anyone else, and press the trigger for two or three seconds, until only liquid comes out.', src: ['KL-MINI', 'ME-TS', 'ME-CLEANER', 'SO-425'] }] },
    { id: 'shutoff-valve', title: 'The trigger valve’s O rings are dry or worn', kind: 'seal-kit', guide: null, part: 'trigger',
      why: [{ text: 'The valve\u2019s O rings keep it shut. Dry or worn, they let liquid past.', src: ['ME-CLEANER', 'ME-KNAP', 'SO-HHTS'] }],
      checks: [
        { text: 'Does liquid come out without you pressing the lever, or keep coming when you let go?', src: ['ME-CLEANER', 'SO-301'] },
        { text: 'If it has a lock that holds the lever down for a long spray, check the lock is off.', src: ['KL-OP', 'KL-MPOP'], types: PF },
      ],
      fix: [
        { text: 'Take the valve apart and grease its O rings. Replace worn O rings, or the whole valve.', src: ['ME-CLEANER', 'SO-VALVE', 'SO-BPTS', 'BI-GS'] },
      ] },
    { id: 'fitting-loose', title: 'A hose or lance connection is loose, or its seal is worn', kind: 'use', guide: null, part: 'hose',
      why: [{ text: 'A leak at a hose connection is often a loose hose or fitting.', src: ['GL-PRIMA8', 'GL-FM30', 'GL-KNAP', 'SO-HHTS'] }],
      checks: [{ text: 'Look where the hose meets the pump, the trigger valve and the lance.', src: ['SO-HHTS', 'SO-425'] }],
      fix: [
        { text: 'Tighten the coupling nut, then test it with water under pressure.', src: ['GL-PRIMA8', 'GL-KNAP'] },
        { text: 'If it still leaks, release the pressure, check the O ring or seal and seat it before you hand tighten.', src: ['SO-REPAIR', 'SO-HHTS'] },
      ] },
    { id: 'hose-worn', title: 'The hose is worn or damaged', kind: 'part', guide: 'repair', part: 'hose',
      why: [{ text: 'Hoses, seals and other parts age with use, depending on what you spray.', src: ['BI-FAQ'] }],
      checks: [{ text: 'Look along the hose for cracks, brittleness, wear and other damage.', src: ['SO-425', 'GL-PRIMA8'] }],
      fix: [{ text: 'Replace a damaged hose straight away, and do not use the sprayer until it is fixed.', src: ['BI-TIPS', 'SO-425', 'SO-BPTS'] }] },
    { id: 'hose-aged', title: 'The hose has aged in the sun or with chemical left in it', kind: 'use', guide: 'care', part: 'hose',
      why: [{ text: 'Chemical left in a sprayer works on the rubber, and sun and frost damage sprayers.', src: ['SO-SEALS', 'NP-RG', 'BI-SAFETY'] }],
      checks: [{ text: 'Is the sprayer stored with chemical in it, in the sun or where it freezes?', src: ['NP-RG', 'BI-SAFETY'] }],
      fix: [{ text: 'Replace the hose. Then rinse the sprayer after every use and store it out of the sun and frost.', src: ['SO-SEALS', 'NP-RG', 'BI-TIPS'] }] },
    { id: 'tank-vent', title: 'Air cannot get back into the tank', kind: 'clean', guide: null, part: null,
      why: [{ text: 'As you spray, air must get back into the tank. If it cannot, the tank pulls in and pumping gets harder.', src: ['SO-BPTS', 'BI-RPD'] }],
      checks: [{ text: 'Check the vent hole or membrane in the filling cap is clean and open, and that it is the sprayer’s own vented cap.', src: ['SO-BPTS', 'BI-RPD'] }],
      fix: [{ text: 'Clean the vent hole. Replace a cap with no vent with the right vented cap.', src: ['SO-BPTS'] }] },
    { id: 'intake-blocked', title: 'The intake is blocked', kind: 'clean', guide: null, part: null,
      why: [{ text: 'A blocked inlet screen, suction hose or intake channel stops the pump drawing liquid.', src: ['SO-BPTS', 'SO-BPMAINT', 'BI-RPD'] }],
      checks: [{ text: 'If the pump handle springs back down when you let go, or the tank will not empty, check the inlet screen at the base of the pressure chamber.', src: ['SO-BPMAINT'] }],
      fix: [
        { text: 'Clean the inlet screen with a small brush and detergent, and clean the intake channels.', src: ['SO-BPTS', 'SO-BPMAINT'] },
        { text: 'Clean the suction hose, or replace it if it is damaged.', src: ['BI-RPD', 'BI-FLOX'] },
      ] },
    { id: 'pump-dirty', title: 'The pump cylinder or its moving parts are dirty or dry', kind: 'clean', guide: 'care', part: null,
      why: [{ text: 'Dirt on the cylinder wall or in the bushes makes the pump drag.', src: ['SO-HHTS', 'SO-BPTS'] }],
      checks: [
        { text: 'Release the pressure, take the pump out and look at the cylinder wall: clean and smooth?', src: ['SO-HHTS'], types: PF },
        { text: 'Check the lever’s bushes and the cylinder for dirt, and the diaphragm and guide bush for grease.', src: ['SO-BPTS', 'ME-KNAP'], types: ['knapsack'] },
      ],
      fix: [
        { text: 'Clean the cylinder and the piston, grease the piston O ring and refit.', src: ['SO-HHTS', 'SO-MAINT'], types: PF },
        { text: 'Clean and grease the bushes, and grease the diaphragm and guide bush.', src: ['SO-BPTS', 'ME-KNAP'], types: ['knapsack'] },
      ] },
    { id: 'wrong-seal', title: 'The seals do not suit the chemical', titles: { trigger: 'The trigger’s seals do not suit the chemical' }, kind: 'seal-material', seal: true, guide: 'repair', guideTypes: P3, part: 'kit',
      why: [
        { text: 'Chemicals can damage the seals inside a sprayer. VITON (FKM) and EPDM seals suit different chemicals, so the seal should match what you spray.', src: ['SO-SEALS', 'ME-CHART'] },
        { text: 'Petroleum based greases swell EPDM and destroy it, which is the same reason EPDM is the wrong seal for petroleum solvents.', src: ['NP-RG'] },
        { text: 'A stronger mix than the chemical maker gives also shortens the life of a sprayer.', src: ['KL-CARE'] },
      ],
      checks: [
        { text: 'Find out from the manual which seal your sprayer has, then check your chemical in the seal finder below.', src: ['NP-RG', 'ME-CHART'], types: P3 },
        { text: 'Find out which seal your trigger has, from its product page or the supplier, then check your chemical in the seal finder below.', src: ['NP-RG', 'BR-APPL'], types: ['trigger'] },
      ],
      fix: [
        { text: 'Fit new seals of the material that suits the chemical, or use a sprayer built for it.', src: ['NP-RG', 'SO-SEALS'], types: P3 },
        { text: 'Replace the trigger with one that suits the chemical.', src: ['NP-RG', 'ME-CHART'], types: ['trigger'] },
        { text: 'If the guide has no rating for your chemical, send us the SDS before you refill.', src: ['NP-RG', 'BR-APPL'] },
      ] },
    { id: 'chem-left', title: 'Chemical was left standing in the sprayer', kind: 'use', guide: 'care', part: 'kit',
      why: [{ text: 'Chemical left in a sprayer works on the seals for days. Rinsing after every use gives them the longest life.', src: ['NP-RG', 'SO-SEALS', 'ME-CLEAN', 'KL-CARE', 'BR-AR5'] }],
      checks: [{ text: 'Was mixed chemical left in it overnight or longer, or was it put away without a rinse?', src: ['SO-SEALS', 'SO-HHTS'] }],
      fix: [
        { text: 'Replace the swollen or damaged seals.', src: ['SO-HHTS', 'SO-BPTS'], types: P3 },
        { text: 'Replace the trigger if it is damaged.', src: ['BI-TIPS'], types: ['trigger'] },
        { text: 'From now on, mix only as much as you need. Where possible, pour what is left back into its own container; if not, dispose of it as the label or SDS says, never down the drain.', src: ['GL-405T', 'KL-OP', 'GL-FM10', 'NP-RG'] },
        { text: 'Rinse the sprayer with clean water after every use, then empty out the rinse water too.', src: ['NP-RG', 'SO-SEALS', 'ME-CLEAN', 'KL-OP'] },
      ] },
    { id: 'grease-wrong', title: 'Petroleum based grease was used on EPDM seals', kind: 'seal-material', guide: 'care', part: 'kit',
      why: [{ text: 'Petroleum based greases swell EPDM and destroy it.', src: ['NP-RG', 'KL-CARE'] }],
      checks: [{ text: 'If your sprayer has EPDM seals, check what has been used to grease them.', src: ['NP-RG'] }],
      fix: [{ text: 'Fit new EPDM seals and use silicone oil only from now on.', src: ['NP-RG', 'KL-REFILL'] }] },
    { id: 'heat-age', title: 'Heat, sun, frost or age', kind: 'part', guide: 'care', part: 'kit',
      why: [{ text: 'Seals dry out with age, and heat, sun and frost damage sprayers. UV turns a sound bottle brittle.', src: ['SO-BPTS', 'NP-RG', 'BI-SAFETY', 'KL-CARE'] }],
      checks: [
        { text: 'Look for cracks and brittle parts.', src: ['GL-PRIMA8'] },
        { text: 'Is the liquid warmer than your sprayer’s manual allows? Klager’s limit is 30 °C; the other makers give 30 to 50 °C, so check yours.', src: ['NP-RG', 'KL-OP', 'BI-FM125', 'SO-201', 'GL-405T'] },
      ],
      fix: [
        { text: 'Replace cracked or brittle seals and parts, and do not use the sprayer until it is fixed.', src: ['GL-PRIMA8', 'BI-TIPS'] },
        { text: 'Store it empty and depressurised, out of heat, frost and direct sun.', src: ['NP-RG', 'SO-MAINT', 'KL-OP', 'BR-APPL'] },
        { text: 'Never leave liquid in it where it can freeze: ice can damage it beyond repair.', src: ['KL-CARE', 'BR-SOX'], types: P3 },
      ] },
    { id: 'mix-lumps', title: 'The mix was not dissolved or filtered', kind: 'use', guide: null, part: null,
      why: [{ text: 'Powder that has not fully dissolved, or a mix poured in unfiltered, blocks the hose and nozzle.', src: ['BI-TIPS', 'BI-FAQ'] }],
      checks: [{ text: 'Was a powder mixed in, or the mix made up in the sprayer itself?', src: ['BI-TIPS', 'BI-FAQ'] }],
      fix: [{ text: 'Mix in a separate container, make sure powders are fully dissolved, and filter the mix as you fill.', src: ['BI-TIPS', 'BI-FAQ'] }] },
    { id: 'low-pressure-spray', title: 'The pressure has dropped as you spray', titles: { knapsack: 'You are not pumping fast enough' }, kind: 'use', guide: 'refill', guideTypes: PF, part: null,
      why: [
        { text: 'Keeping the pressure up keeps the pattern even. As it drops, the spray or the foam gets smaller.', src: ['NP-RG', 'ME-FOAMER', 'KL-FOAM', 'BR-AR5', 'BR-FOAM'], types: PF },
        { text: 'If the pressure falls below the regulator’s setting, the regulator shuts and no spray comes out.', src: ['GL-KNAP'], types: ['knapsack'] },
      ],
      checks: [{ text: 'Is the spray, or the foam, getting smaller as you go?', src: ['ME-FOAMER', 'NP-RG'], types: PF },
        { text: 'Does the spray stop while the lever is still?', src: ['GL-KNAP'], types: ['knapsack'] }],
      fix: [
        { text: 'Top the pressure up with a few strokes during the job.', src: ['NP-RG', 'ME-FOAMER', 'BR-AR5', 'BR-FOAM'], types: PF },
        { text: 'Pump faster as you spray.', src: ['GL-KNAP'], types: ['knapsack'] },
      ] },
    { id: 'foam-pad', title: 'The foam pad in the nozzle is clogged', kind: 'clean', guide: null, part: 'nozzle',
      why: [{ text: 'The foam pad in the nozzle has a big effect on the foam. Clogged, it gives poor foam.', src: ['ME-FOAMER', 'SO-303F'] }],
      checks: [{ text: 'Look at the foam pad or felt in the foam nozzle.', src: ['SO-303F', 'ME-FOAMER'] }],
      fix: [{ text: 'Clean the foam pad with clean water and a soft brush, or replace it.', src: ['SO-303F', 'ME-FOAMER', 'KL-FOAM'] }] },
    { id: 'foam-air', title: 'The air holes at the foam nozzle are blocked', kind: 'clean', guide: null, part: null,
      why: [{ text: 'Clogged air inlet holes where the foam nozzle connects make foaming less effective.', src: ['GL-FM10', 'GL-FM30'] }],
      checks: [{ text: 'Check the small air inlet holes where the foam nozzle connects.', src: ['GL-FM10', 'GL-FM30'] }],
      fix: [{ text: 'Clean the holes out.', src: ['GL-FM10', 'GL-FM30'] }] },
    { id: 'riser-off', title: 'The tube inside the tank has come off', kind: 'use', guide: null, part: null,
      why: [{ text: 'If the riser tube is off the hose line, or not in place, there is no foam.', src: ['ME-FOAMER'] }],
      checks: [{ text: 'Release the pressure, open the tank and check the riser tube is on the hose line.', src: ['ME-FOAMER'] }],
      fix: [{ text: 'Connect the riser tube to the hose line, or screw the hose line back into the tank.', src: ['ME-FOAMER'] }] },
    { id: 'not-foam', title: 'The product is not made to foam', kind: 'use', guide: null, part: null,
      why: [{ text: 'Only a product made to foam will foam. Others need a foaming agent added.', src: ['KL-FOAM', 'BR-FOAM'] }],
      checks: [{ text: 'Is your product one made for foaming?', src: ['KL-FOAM', 'BR-FOAM'] }],
      fix: [{ text: 'Use a product made for foaming, or ask its maker about adding a foaming agent.', src: ['BR-FOAM', 'KL-FOAM'] }] },
    { id: 'too-thick', title: 'The liquid is too thick or sticky to spray', kind: 'use', guide: null, part: null,
      why: [{ text: 'An even spray needs a liquid that is clean and runs freely. Thick or sticky liquids do not suit these sprayers.', src: ['KL-MPOP', 'GL-PRIMA8', 'BR-AR5'] }],
      checks: [{ text: 'Is the liquid thick or sticky, or does it leave a residue that will not rinse out?', src: ['GL-PRIMA8', 'BR-AR5'] }],
      fix: [{ text: 'Ask the chemical maker how to thin it for spraying, or ask us about a sprayer that suits it.', src: ['BR-SURE'] }] },
    { id: 'trigger-loose', title: 'The trigger is loose on the bottle, or its seal is out of place', kind: 'use', guide: null, part: null,
      why: [{ text: 'A leak at a connection is often a cap that is not tight, or a seal that is damaged or not sitting flat.', src: ['SO-REPAIR', 'GL-PRIMA8'] }],
      checks: [{ text: 'Check the trigger is screwed down tight, and the seal inside its cap sits flat and is not damaged.', src: ['SO-REPAIR'] }],
      fix: [{ text: 'Tighten the trigger by hand. If it still leaks, check the sealing surface and replace a damaged seal or the trigger.', src: ['SO-REPAIR', 'BI-TIPS'] }] },
    { id: 'trigger-worn', title: 'The trigger is worn or damaged', kind: 'part', guide: null, part: 'trigger',
      why: [{ text: 'On a trigger bottle, it is usually the trigger or the bottle that gives out.', src: ['NP-RG'] }],
      checks: [{ text: 'Look for cracks or damage on the trigger and the bottle.', src: ['GL-PRIMA8', 'BI-TIPS'] }],
      fix: [{ text: 'Replace a damaged trigger or bottle straight away.', src: ['BI-TIPS', 'NP-RG'] }] },
    { id: 'tube-blocked', title: 'The suction tube is blocked', kind: 'clean', guide: null, part: null,
      why: [{ text: 'A blocked suction tube, or the filter on its end, gives little or no spray.', src: ['BI-GS', 'ME-CLEANER'] }],
      checks: [{ text: 'Unscrew the trigger and look at the tube and any filter on its end.', src: ['BI-GS', 'ME-CLEANER'] }],
      fix: [{ text: 'Rinse the tube and filter clean under running water.', src: ['BI-GS', 'ME-CLEANER'] }] },
  ];

  // Most likely first, for each sprayer type and problem.
  var ORDER = {
    trigger: {
      'leak-trigger': ['trigger-loose', 'trigger-worn', 'wrong-seal', 'chem-left'],
      'no-pressure': ['nozzle-blocked', 'tube-blocked', 'trigger-worn'],
      uneven: ['nozzle-blocked', 'mix-lumps', 'too-thick', 'trigger-worn'],
      seals: ['wrong-seal', 'chem-left', 'heat-age'],
    },
    pressure: {
      'leak-trigger': ['shutoff-valve', 'air-in-line', 'nozzle-blocked', 'wrong-seal'],
      'leak-pump': ['cap-loose', 'cap-seal', 'relief-valve', 'valve-dirty', 'wrong-seal'],
      'leak-hose': ['fitting-loose', 'hose-worn', 'wrong-seal'],
      'no-pressure': ['cap-loose', 'piston-oring', 'valve-dirty', 'relief-valve', 'no-air-buffer'],
      'loses-pressure': ['piston-oring', 'cap-seal', 'cap-loose', 'no-air-buffer', 'relief-valve'],
      uneven: ['nozzle-blocked', 'filter-blocked', 'low-pressure-spray', 'air-in-line', 'mix-lumps', 'too-thick'],
      seals: ['wrong-seal', 'chem-left', 'grease-wrong', 'heat-age'],
      'hose-collapsed': ['hose-worn', 'hose-aged'],
      'pump-hard': ['piston-oring', 'pump-dirty', 'chem-left'],
    },
    knapsack: {
      'leak-trigger': ['shutoff-valve', 'air-in-line', 'nozzle-blocked'],
      'leak-pump': ['pump-leak', 'cap-seal', 'wrong-seal'],
      'leak-hose': ['fitting-loose', 'hose-worn', 'wrong-seal'],
      'no-pressure': ['piston-oring', 'valve-dirty', 'intake-blocked'],
      'loses-pressure': ['air-chamber-full', 'low-pressure-spray', 'valve-dirty'],
      uneven: ['nozzle-blocked', 'filter-blocked', 'low-pressure-spray', 'mix-lumps', 'too-thick'],
      seals: ['wrong-seal', 'chem-left', 'grease-wrong', 'heat-age'],
      'hose-collapsed': ['tank-vent', 'intake-blocked', 'hose-worn'],
      'pump-hard': ['pump-dirty', 'piston-oring', 'air-chamber-full', 'tank-vent', 'intake-blocked'],
    },
    foamer: {
      'leak-trigger': ['shutoff-valve', 'nozzle-blocked', 'wrong-seal'],
      'leak-pump': ['cap-loose', 'cap-seal', 'relief-valve', 'valve-dirty', 'wrong-seal'],
      'leak-hose': ['fitting-loose', 'hose-worn', 'wrong-seal'],
      'no-pressure': ['cap-loose', 'piston-oring', 'valve-dirty', 'relief-valve', 'no-air-buffer'],
      'loses-pressure': ['piston-oring', 'cap-seal', 'cap-loose', 'no-air-buffer', 'relief-valve'],
      uneven: ['foam-pad', 'foam-air', 'low-pressure-spray', 'not-foam', 'nozzle-blocked', 'filter-blocked', 'riser-off'],
      seals: ['wrong-seal', 'chem-left', 'grease-wrong', 'heat-age'],
      'hose-collapsed': ['hose-worn', 'hose-aged'],
      'pump-hard': ['piston-oring', 'pump-dirty', 'chem-left'],
    },
  };

  // Parts the seal finder may show, by seal and sprayer type. The live product page must still say the seal, and what
  // it fits is the product page's own words (fix-engine.js). Read from the shop on 19 Oct 2026 (kits-snapshot.json).
  // The GLORIA brass pump kits are left out: GLORIA rules out acids, alkalis and solvents for its brass pump sprayers
  // (GL-405T), and every seal answer is for one of those (SF-08).
  var KITS = [
    { handle: 'klager-viton-seal-kit', seal: 'VITON', types: ['pressure'] },
    { handle: 'klager-fkm-80-seal-kit', seal: 'VITON', types: ['pressure'] },
    { handle: 'gloria-i-genuine-parts-accessories-complete-viton-seals-kit-for-acid-resistant-sprayers', seal: 'VITON', types: ['pressure'] },
    { handle: 'gloria-i-genuine-parts-accessories-viton-seals-kit-for-pro-sprayers-with-nylon-rod', seal: 'VITON', types: ['pressure', 'foamer'] },
    { handle: 'gloria-i-genuine-parts-accessories-viton-seals-kit-for-pro-sprayers-with-steel-rod', seal: 'VITON', types: ['pressure'] },
    { handle: 'gloria-i-genuine-parts-accessories-pressure-release-valve-viton', seal: 'VITON', types: ['pressure', 'foamer'] },
    { handle: 'genuine-parts-accessories-acid-resistant-trigger-with-viton-seals', seal: 'VITON', types: ['pressure'] },
    { handle: 'gloria-i-genuine-parts-accessories-pro-sprayers-pump-with-viton-seals', seal: 'VITON', types: ['pressure'] },
    { handle: 'klager-epdm-seal-kit', seal: 'EPDM', types: ['pressure'] },
    { handle: 'gloria-i-genuine-parts-accessories-epdm-seals-kit-for-cm50-alkaline-resistant-sprayers', seal: 'EPDM', types: ['pressure'] },
    { handle: 'nora-pro-alkaline-resistant-trigger-with-epdm-seals', seal: 'EPDM', types: ['pressure'] },
    { handle: 'gloria-i-genuine-parts-accessories-pressure-release-valve-epdm', seal: 'EPDM', types: ['pressure'] },
    { handle: 'gloria-i-genuine-parts-accessories-pro-sprayers-pump-with-epdm-seals', seal: 'EPDM', types: ['pressure'] },
  ];

  var RATING = { suitable: 'Suitable', conditional: 'Conditional', ask: 'Ask us' };

  // What the quote form says the visitor is after, from the most likely cause.
  var PARTS = { triggerBottle: 'Trigger sprayer', kit: 'Seal kit', valve: 'Pressure relief valve', pump: 'Pump or pump seals', trigger: 'Trigger or trigger valve', hose: 'Hose', nozzle: 'Nozzle or lance', sprayer: 'Sprayer' };

  var TEXT = {
    oilsAsk: 'The guide rates {name} on its own. Your product also has oils or solvents, and the guide has no rating for that mix, so we will not guess. Send us the SDS, or look up the solvent in the Chemical Compatibility Guide too.',
    oilsSolvent: 'The rating is for {name} itself. If your product mixes it with other chemicals, send us the SDS.',
    oilsNarrow: 'Your product also has oils or solvents, so the answer is the solvent resistant sprayer only: the guide does not rate the others for oils and petroleum solvents, and EPDM is the wrong seal for them.',
    badStrengthLeft: 'Strength left out: this answer is for the chemical without a strength. Fix the box to check your strength.',
    otherMatches: 'Other matches',
    oilsUnsure: 'Check section 3 of the SDS for oils or solvents. If it has any, this answer may not apply, so ask us.',
    noMatch: 'That is not in our Chemical Compatibility Guide, so we will not guess a seal.',
    noMatchNote: 'From the NORA PRO sprayer troubleshooter: my chemical is not in the Chemical Compatibility Guide. Which seal does it need? I will send the SDS.',
    noRating: 'Our sources do not rate a sprayer for this. Ask us with the SDS.',
    needStrength: 'Add the strength from the label or SDS: the answer depends on it.',
    pctHint: 'The per cent from the label or SDS, for example 10.',
    phHint: 'From the label or SDS, 0 to 14, for example 11.',
    badPct: 'Type the strength as a per cent above 0 and up to 100, for example 10. It is left out until then.',
    badPh: 'Type the pH as a number from 0 to 14, for example 11. It is left out until then.',
    sealCaveat: 'Guidance only. The answer depends on the exact product, its strength, the temperature and how long it stays in the sprayer. Check the SDS.',
    buyAsk: 'The guide has no rating for this, so we will not suggest a seal kit. Send us the SDS and your sprayer model and we will check it.',
    fitsRule: 'We only list a part when its product page says this seal. What it fits is what the product page says; tell us your model and we will confirm.',
    noKit: 'We do not list a part with this seal for a {type}. Tell us your model and we will check what fits.',
    noFits: 'The product page does not name a model, so tell us yours and we will confirm.',
    partsFailed: 'We could not load our parts list just now. See all parts and seal kits below, or ask us.',
    printFoot: 'NORA PRO sprayer troubleshooter. A guide only: the chemical label, the SDS and your sprayer’s manual come first.',
  };

  return {
    SOURCES: SOURCES, TYPES: TYPES, SYMPTOMS: SYMPTOMS, CAUSES: CAUSES, ORDER: ORDER, SAFETY: SAFETY, KITS: KITS,
    RATING: RATING, PARTS: PARTS, TEXT: TEXT, GUIDE: GUIDE,
  };
});

/*
 * NORA PRO Sprayer Troubleshooter and Seal Finder (the rules, no page code).
 * Used by:
 *   - the page UI (theme asset assets/nora-sprayer-fix.js, built by tools/sprayer-fix/build.mjs after
 *     tools/chem-guide/engine.js, fix-data.js and this file)
 *   - the tests (test-engine.mjs, mutation-test.mjs) and the page copy (build-copy.mjs)
 * Repair advice comes only from fix-data.js, where every line names its source (SOURCES.md has the exact words).
 * Seal answers come only from the Chemical Compatibility Guide dataset, through its own engine (NoraChem).
 * Nothing in here invents a rating or says a part fits a model.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory(require('./fix-data.js'));
  else root.NoraFix = factory(root.NoraFixData);
})(typeof self !== 'undefined' ? self : this, function (D) {
  'use strict';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function byKey(list, id) {
    for (var i = 0; i < list.length; i += 1) if (list[i].id === id) return list[i];
    return null;
  }
  function type(id) { return byKey(D.TYPES, id); }
  function symptom(id) { return byKey(D.SYMPTOMS, id); }
  function cause(id) { return byKey(D.CAUSES, id); }

  // The symptoms that apply to a sprayer type, in the order shown.
  function symptomsFor(t) {
    return D.SYMPTOMS.filter(function (s) { return !!(D.ORDER[t] && D.ORDER[t][s.id]); });
  }
  // A symptom's wording for a type (a trigger bottle has a trigger head, a pressure sprayer a trigger valve).
  function symptomName(s, t) { return (s.names && s.names[t]) || s.name; }

  // One advice line applies to a type when it names no types, or names this one.
  function forType(lines, t) {
    return (lines || []).filter(function (l) { return !l.types || l.types.indexOf(t) > -1; });
  }

  // The likely causes for a sprayer type and a symptom, most likely first, with the checks and the fix for each.
  // Returns null when the pair is not one the tool covers.
  function diagnose(t, s) {
    var ty = type(t);
    var sy = symptom(s);
    var order = ty && sy && D.ORDER[t] ? D.ORDER[t][s] : null;
    if (!order) return null;
    var causes = order.map(function (id, i) {
      var c = cause(id);
      return {
        id: c.id, rank: i + 1, title: (c.titles && c.titles[t]) || c.title, why: forType(c.why, t),
        checks: forType(c.checks, t), fix: forType(c.fix, t), kind: c.kind, seal: !!c.seal, part: c.part || null,
        guide: c.guide && (!c.guideTypes || c.guideTypes.indexOf(t) > -1) ? c.guide : null, src: sources(c, t),
      };
    });
    var seal = causes.some(function (c) { return c.seal; });
    return {
      type: ty.id, typeName: ty.name, symptom: sy.id, symptomName: symptomName(sy, t), causes: causes, seal: seal,
      safety: forType(D.SAFETY, t), note: forType(sy.notes, t),
    };
  }

  // Every source named by a cause's lines for this type, once each, in the order they first appear.
  function sources(c, t) {
    var out = [];
    ['why', 'checks', 'fix'].forEach(function (k) {
      forType(c[k], t).forEach(function (l) { (l.src || []).forEach(function (id) { if (out.indexOf(id) < 0) out.push(id); }); });
    });
    return out;
  }
  function sourceInfo(id) { return D.SOURCES[id] || null; }

  // ---------------------------------------------------------------- seal finder

  // oils: 'yes', 'no', 'unsure' or '' (not answered). The guide rates each chemical on its own, so a product that also
  // has oils or solvents is only covered when the chemical is itself one the solvent sprayer is rated for.
  function seal(E, data, row, opts) {
    opts = opts || {};
    var strength = typeof opts.strength === 'number' && isFinite(opts.strength) ? opts.strength : null;
    var r = E.resolve(data, row, { strength: strength, hot: !!opts.hot });
    var out = {
      id: row.id, name: row.name, rating: r.rating, fams: r.fams.slice(), famLabels: r.famLabels.slice(),
      seal: r.fams.length ? r.seal : null, text: r.text, note: r.note, spec: r.spec, max: r.max, hot: r.hot,
      needsStrength: r.needsStrength, scale: row.scale || null, strength: r.strength, caveats: [], oils: opts.oils || '',
      src: (row.src || []).slice(), col: r.col, mixed: false,
    };
    var name = lowerFirst(row.name);
    var solvent = out.fams.indexOf('solvent') > -1;
    if (opts.oils === 'yes' && !solvent && out.rating !== 'ask') {
      out.rating = 'ask';
      out.fams = [];
      out.famLabels = [];
      out.seal = null;
      out.mixed = true;
      out.text = D.TEXT.oilsAsk.replace('{name}', name);
    } else if (opts.oils === 'yes' && solvent) {
      // The guide rates the chemical in several builds, but with oils or petroleum solvents in the product only the
      // solvent resistant build is rated for the rest of the mix: the others are dropped, never added (SF-01).
      if (out.fams.length > 1) {
        out.famLabels = [out.famLabels[out.fams.indexOf('solvent')]];
        out.fams = ['solvent'];
        out.seal = null;
        out.mixed = true;
        out.text = D.TEXT.oilsNarrow;
      }
      out.caveats.push(D.TEXT.oilsSolvent.replace('{name}', name));
    } else if (opts.oils === 'unsure' && out.rating !== 'ask') {
      out.caveats.push(D.TEXT.oilsUnsure);
    }
    out.groups = sealGroups(data, out.fams);
    return out;
  }

  // The seal groups (VITON, EPDM) that the answer's sprayer families use.
  function sealGroups(data, fams) {
    var g = [];
    (fams || []).forEach(function (f) { var x = data.families[f] && data.families[f].sealGroup; if (x && g.indexOf(x) < 0) g.push(x); });
    return g;
  }

  // Swollen, soft or cracked seals: the troubleshooter's words for what the wrong seal does, from the guide and the
  // product pages, and only when a seal answer exists.
  function sealWords(ans) {
    if (!ans || !ans.groups.length) return '';
    return ans.groups.join(' or ');
  }

  // ---------------------------------------------------------------- parts we stock (read live from the shop)

  // The parts and seal kits to look up for a seal group and a sprayer type. The shop's own product data then decides:
  // a part is shown only when its live product page says this seal, and what it fits is the product page's own words.
  function kitCandidates(groups, t) {
    return D.KITS.filter(function (k) {
      return groups.indexOf(k.seal) > -1 && (!t || k.types.indexOf(t) > -1);
    });
  }

  function strip(h) {
    return String(h || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&#39;|&rsquo;/g, "'")
      .replace(/&quot;/g, '"').replace(/\s+/g, ' ').trim();
  }
  // One cell from the spec table at the end of a product description: <td><strong>Fits</strong></td><td>...</td>.
  function specCell(html, label) {
    var re = new RegExp('<td[^>]*>\\s*(?:<strong>)?\\s*' + label + '\\s*(?:</strong>)?\\s*</td>\\s*<td[^>]*>([\\s\\S]*?)</td>', 'i');
    var m = re.exec(String(html || ''));
    return m ? strip(m[1]) : '';
  }
  // A product as the shop serves it at /products/<handle>.js, reduced to what the tool shows.
  function readProduct(p) {
    if (!p || typeof p !== 'object' || !p.handle || !p.title) return null;
    return {
      handle: String(p.handle), title: strip(p.title), url: '/products/' + String(p.handle), available: p.available !== false,
      seal: specCell(p.description, 'Seal Type'), fits: specCell(p.description, 'Fits'), part: specCell(p.description, 'Part Number'),
    };
  }
  // The live product says this seal: VITON (FKM, FKM-80) or EPDM.
  function saysSeal(prod, group) {
    var s = String(prod && prod.seal || '').toUpperCase();
    if (!s) return false;
    if (group === 'VITON') return /VITON|FKM/.test(s) && !/EPDM/.test(s);
    if (group === 'EPDM') return /EPDM/.test(s) && !/VITON|FKM/.test(s);
    return false;
  }

  // ---------------------------------------------------------------- share link

  var KEYS = { t: 'type', s: 'symptom', c: 'chem', o: 'oils', h: 'hot' };
  function encode(st) {
    var p = ['fix'];
    if (st.type) p.push('t=' + st.type);
    if (st.symptom) p.push('s=' + st.symptom);
    if (st.chem) p.push('c=' + st.chem + (typeof st.strength === 'number' && isFinite(st.strength) ? ':' + st.strength : ''));
    if (st.oils) p.push('o=' + { yes: 'y', no: 'n', unsure: 'u' }[st.oils]);
    if (st.hot) p.push('h=1');
    return '#' + p.join('&');
  }
  // Anything unknown is dropped: a link can only pick from the tool's own lists.
  function decode(hash) {
    var out = { type: null, symptom: null, chem: null, strength: null, oils: '', hot: false };
    var h = String(hash || '').replace(/^#/, '');
    if (h.split('&')[0] !== 'fix') return null;
    h.split('&').slice(1).forEach(function (kv) {
      var i = kv.indexOf('=');
      if (i < 1) return;
      var k = kv.slice(0, i);
      var v = kv.slice(i + 1);
      try { v = decodeURIComponent(v); } catch (e) { return; }
      if (!KEYS[k]) return;
      if (k === 't' && type(v)) out.type = v;
      if (k === 's' && symptom(v)) out.symptom = v;
      if (k === 'c') {
        var bits = v.split(':');
        var id = bits[0].replace(/[^a-z0-9-]/g, '').slice(0, 60);
        if (id) out.chem = id;
        var n = bits.length > 1 ? parseFloat(bits[1]) : NaN;
        if (isFinite(n) && n >= 0 && n <= 100) out.strength = n;
      }
      if (k === 'o') out.oils = { y: 'yes', n: 'no', u: 'unsure' }[v] || '';
      if (k === 'h') out.hot = v === '1';
    });
    if (out.symptom && (!out.type || !D.ORDER[out.type][out.symptom])) out.symptom = null;
    return out;
  }

  // ---------------------------------------------------------------- strength

  // A strength typed by the visitor: "10", "10%", "12.5 %", "pH 11", "1,5". Returns a number or null, with why.
  function readStrength(text, scale) {
    var t = String(text == null ? '' : text).trim();
    if (!t) return { value: null, error: null };
    var s = t.replace(/^ph\s*/i, '').replace(/\s*%$/, '').replace(/\s*per\s*cent$/i, '').trim();
    // A comma before one or two digits is a decimal point (12,5); "1,000" is not read as 1.
    if (/^\d+,\d{1,2}$/.test(s)) s = s.replace(',', '.');
    if (!/^\d*\.?\d+$/.test(s)) return { value: null, error: scale === 'pH' ? D.TEXT.badPh : D.TEXT.badPct };
    var n = parseFloat(s);
    if (scale === 'pH' && (n < 0 || n > 14)) return { value: null, error: D.TEXT.badPh };
    if (scale !== 'pH' && (n <= 0 || n > 100)) return { value: null, error: D.TEXT.badPct };
    return { value: n, error: null };
  }
  function fmtStrength(n, scale) {
    if (n === null || n === undefined) return '';
    var r = Math.round(n * 100) / 100;
    if (r === 0 && n > 0) return scale === 'pH' ? 'pH below 0.01' : 'below 0.01%';
    var v = String(r);
    return scale === 'pH' ? 'pH ' + v : v + '%';
  }

  // ---------------------------------------------------------------- quote and copy

  // Lower case for the middle of a sentence, keeping VITON, EPDM and O ring as they are.
  function lowerFirst(t) { t = String(t || ''); return /^(VITON|EPDM|O ring|PH|SDS)/.test(t) ? t : t.charAt(0).toLowerCase() + t.slice(1); }

  function quote(diag, ans) {
    var q = {};
    var note = 'From the NORA PRO sprayer troubleshooter.';
    if (diag) {
      note += ' ' + diag.typeName + ': ' + lowerFirst(diag.symptomName) + '. Most likely: ' + lowerFirst(diag.causes[0].title) + '.';
      // A trigger bottle is replaced, not re-sealed; otherwise the most likely cause's part, if it has one (SF-17).
      if (diag.type === 'trigger') q.product = D.PARTS.triggerBottle;
      else if (diag.causes[0].part) q.product = D.PARTS[diag.causes[0].part];
    }
    if (ans) {
      var chem = ans.name + (ans.strength !== null && ans.strength !== undefined ? ' (' + fmtStrength(ans.strength, ans.scale) + ')' : '');
      q.chemical = chem;
      note += ' Seal finder: ' + chem + ', ' + (ans.groups.length ? ans.groups.join(' or ') + ' (' + D.RATING[ans.rating].toLowerCase() + ')' : 'ask us') + '.';
      if (ans.oils === 'yes') note += ' It also has oils or solvents.';
      if (ans.hot) note += ' The liquid is warmer than 30 °C.';
      if (ans.groups.length === 1) q.seal = ans.groups[0];
      // No seal answer, so no seal kit is suggested (TEXT.buyAsk).
      if (!ans.groups.length && q.product === D.PARTS.kit) delete q.product;
      if (ans.famLabels.length && !q.product) q.recommended = ans.famLabels.join(' or ');
    }
    q.note = note;
    return q;
  }
  function quoteUrl(base, q) {
    var parts = [];
    ['product', 'recommended', 'chemical', 'seal', 'note'].forEach(function (k) { if (q[k]) parts.push(k + '=' + encodeURIComponent(q[k])); });
    return base + (base.indexOf('?') > -1 ? '&' : '?') + parts.join('&');
  }

  // Copied or printed steps leave the page, so "the seal finder below" says where it is (SF-14).
  function offPage(t) { return String(t).replace(/in the seal finder below/g, 'in the seal finder at norapro.com.au/pages/sprayer-troubleshooter'); }

  // Plain text for the Copy button: what a person would paste into a message or a job note.
  function text(diag, ans, link) {
    var out = [];
    if (diag) {
      out.push(diag.typeName + ': ' + diag.symptomName);
      out.push('Safety first: ' + diag.safety.map(function (l) { return l.text; }).join(' '));
      diag.causes.forEach(function (c) {
        out.push('');
        out.push(c.rank + '. ' + c.title);
        c.checks.forEach(function (l) { out.push('Check: ' + offPage(l.text)); });
        c.fix.forEach(function (l) { out.push('Fix: ' + offPage(l.text)); });
      });
    }
    if (ans) {
      if (out.length) out.push('');
      out.push('Seal for ' + ans.name + (ans.strength !== null && ans.strength !== undefined ? ' at ' + fmtStrength(ans.strength, ans.scale) : '') + ': ' +
        (ans.groups.length ? ans.groups.join(' or ') + ' (' + D.RATING[ans.rating] + ')' : 'ask us, the guide has no rating for this'));
      if (ans.text) out.push(ans.text);
      ans.caveats.forEach(function (c) { out.push(c); });
    }
    if (link) { out.push(''); out.push(link); }
    out.push('NORA PRO sprayer troubleshooter. A guide only: the chemical label and SDS come first.');
    return out.join('\n');
  }

  return {
    TYPES: D.TYPES, SYMPTOMS: D.SYMPTOMS, CAUSES: D.CAUSES, ORDER: D.ORDER, SAFETY: D.SAFETY, SOURCES: D.SOURCES, KITS: D.KITS,
    TEXT: D.TEXT, RATING: D.RATING, PARTS: D.PARTS, GUIDE: D.GUIDE,
    esc: esc, type: type, symptom: symptom, cause: cause, symptomsFor: symptomsFor, symptomName: symptomName, diagnose: diagnose,
    sourceInfo: sourceInfo, seal: seal, sealGroups: sealGroups, sealWords: sealWords, kitCandidates: kitCandidates,
    readProduct: readProduct, specCell: specCell, saysSeal: saysSeal, encode: encode, decode: decode, readStrength: readStrength,
    fmtStrength: fmtStrength, quote: quote, quoteUrl: quoteUrl, text: text, lowerFirst: lowerFirst, offPage: offPage,
  };
});

/*
 * NORA PRO Sprayer Troubleshooter and Seal Finder (page UI).
 * Built into assets/nora-sprayer-fix.js after tools/chem-guide/engine.js, fix-data.js and fix-engine.js by
 * tools/sprayer-fix/build.mjs. The rules live in fix-engine.js and fix-data.js.
 * Network: the chemical dataset (assets/nora-chemicals.json, loaded once the page is idle) and the shop's own
 * product pages (/products/<handle>.js) for the parts we stock. Nothing the visitor types is sent anywhere.
 */
(function () {
  'use strict';
  var root = document.getElementById('nora-fix');
  if (!root || typeof NoraFix === 'undefined') return;
  var F = NoraFix;
  var E = typeof NoraChem !== 'undefined' ? NoraChem : null;
  var jsonUrl = root.getAttribute('data-json');
  var quoteBase = root.getAttribute('data-quote-url') || '/pages/request-a-quote';
  var guideUrl = root.getAttribute('data-guide-url') || '/pages/chemical-compatibility-guide';
  var refillUrl = root.getAttribute('data-refill-url') || '/pages/refill-and-repair-guide';
  var partsUrl = root.getAttribute('data-parts-url') || '/collections/parts-seal-repair-kits';
  // A trigger bottle's parts are its trigger: our trigger sprayers, not the pump up sprayer parts (SF-07).
  var triggersUrl = root.getAttribute('data-triggers-url') || '/collections/trigger-sprayers';

  function $(sel) { return root.querySelector(sel); }
  function $$(sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  // Writes the answer bar's text only when it changes. Rewriting it on every focus change replaced the element under
  // the pointer between mouse down and mouse up, so the browser dropped the click (ALL-R1).
  function setHtml(el, h) { if (el && el.__h !== h) { el.innerHTML = h; el.__h = h; } }
  var esc = F.esc;
  function val(id) { var x = document.getElementById(id); return x ? x.value : ''; }

  var state = {
    type: null, symptom: null, chem: null, strengthText: '', oils: '', hot: false, data: null, loading: null,
    used: false, toolUse: false, edited: false, focus: 'fix', diag: null, ans: null, timer: null, sealTimer: null,
    linkTimer: null, lastSig: '', lastSealSig: '', headerH: 0, fromFix: false, parts: {}, partsFor: '',
  };

  // ---------------------------------------------------------------- analytics (same GTM tag as the guide)

  // Every guide parameter is set on every push (empty when it does not apply), because GTM merges pushes and a later
  // event would otherwise keep an earlier value. Counts and kinds only: never what the visitor types.
  function track(event, params) {
    try {
      var payload = { event: event, guide_mode: 'fix' };
      ['guide_chemical', 'guide_rating', 'guide_strength_given', 'guide_list_size', 'guide_via', 'guide_restored', 'guide_units_needed', 'guide_mixed_seals', 'guide_ask_us', 'guide_kind', 'guide_rows']
        .forEach(function (k) { payload[k] = undefined; });
      Object.keys(params || {}).forEach(function (k) { if (params[k] !== undefined && params[k] !== null && params[k] !== '') payload[k] = params[k]; });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) { /* never break the tool for analytics */ }
  }
  function firstUse(kind) {
    state.used = true;
    state.edited = true;
    if (state.toolUse) return;
    state.toolUse = true;
    track('guide_tool_use', { guide_kind: kind });
  }

  // ---------------------------------------------------------------- dataset (lazy, as the Dilution Calculator)

  function load() {
    if (state.data) return Promise.resolve(state.data);
    if (state.loading) return state.loading;
    if (!E || !jsonUrl || !window.fetch) return Promise.resolve(null);
    state.loading = fetch(jsonUrl)
      .then(function (r) { if (!r.ok) throw new Error('dataset ' + r.status); return r.json(); })
      .then(function (d) { state.data = d; return d; })
      .catch(function () { state.loading = null; return null; });
    return state.loading;
  }

  // ---------------------------------------------------------------- icons (drawn for this tool, no brand shapes)

  var ICON = {
    trigger: '<svg viewBox="0 0 40 40" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"><path d="M14 8h11l5 4h3"/><path d="M17 8v5h7l-2 5"/><path d="M15 13v4"/><path d="M13 17h11l2 4v13a2 2 0 0 1-2 2H13a2 2 0 0 1-2-2V21z"/></svg>',
    pressure: '<svg viewBox="0 0 40 40" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"><path d="M18 3h4v8"/><path d="M14 11h12"/><path d="M12 14h16l1 3v16a3 3 0 0 1-3 3H14a3 3 0 0 1-3-3V17z"/><path d="M28 18c5 1 7 4 7 8"/><path d="M35 26l2 5"/></svg>',
    knapsack: '<svg viewBox="0 0 40 40" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"><path d="M10 9a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v24a3 3 0 0 1-3 3H13a3 3 0 0 1-3-3z"/><path d="M14 5v6h8V5"/><path d="M26 14h4v14"/><path d="M30 28c0 4 2 6 5 7"/><path d="M10 18h16"/></svg>',
    foamer: '<svg viewBox="0 0 40 40" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"><path d="M14 4h4v8"/><path d="M10 12h12"/><path d="M8 15h16v18a3 3 0 0 1-3 3H11a3 3 0 0 1-3-3z"/><path d="M24 19h5l3-3"/><circle cx="34" cy="12" r="2"/><circle cx="36" cy="18" r="1.6"/><circle cx="31" cy="8" r="1.4"/></svg>',
  };

  // ---------------------------------------------------------------- troubleshooter

  function renderTypes() {
    $('#sf-types').innerHTML = F.TYPES.map(function (t) {
      return '<label class="sf-tile"><input type="radio" name="sf-type" value="' + esc(t.id) + '"' + (state.type === t.id ? ' checked' : '') + '>' +
        (ICON[t.id] || '') + '<span><b>' + esc(t.name) + '</b><small>' + esc(t.hint) + '</small></span></label>';
    }).join('');
  }

  function renderSymptoms() {
    var group = $('#sf-sym-group');
    var wait = $('#sf-sym-wait');
    if (!state.type) { group.hidden = true; wait.hidden = false; return; }
    wait.hidden = true;
    group.hidden = false;
    var list = F.symptomsFor(state.type);
    $('#sf-syms').innerHTML = list.map(function (s) {
      return '<label class="sf-sym"><input type="radio" name="sf-sym" value="' + esc(s.id) + '"' + (state.symptom === s.id ? ' checked' : '') + '>' +
        '<span>' + esc(F.symptomName(s, state.type)) + '</span></label>';
    }).join('');
  }

  function srcLine(ids) {
    var names = ids.map(function (id) { var s = F.sourceInfo(id); return s ? s.short : null; }).filter(Boolean);
    return names.length ? '<p class="sf-src">From: ' + esc(names.join('; ')) + '.</p>' : '';
  }
  function guideHref(anchor) { return refillUrl + (anchor ? '#' + anchor : ''); }

  function renderFix() {
    var out = $('#sf-fix-out');
    var wait = $('#sf-fix-wait');
    var d = state.type && state.symptom ? F.diagnose(state.type, state.symptom) : null;
    state.diag = d;
    if (!d) {
      out.innerHTML = '';
      wait.hidden = false;
      wait.textContent = state.type ? 'Now pick what it is doing, and the likely causes show here, most likely first.' : 'Pick your sprayer and the problem, and the likely causes show here, most likely first.';
      return;
    }
    wait.hidden = true;
    var html = '<div class="sf-safety" role="note"><b>Before you start</b><ul>' +
      d.safety.map(function (l) { return '<li>' + esc(l.text) + '</li>'; }).join('') + '</ul></div>' +
      '<p class="sf-sr">' + esc(d.typeName + ', ' + d.symptomName.toLowerCase() + ': ' + d.causes.length + ' likely causes, most likely first.') + '</p>' +
      '<ol class="sf-causes">' + d.causes.map(function (c) {
        var parts = [];
        if (c.guide) parts.push('<a class="sf-link" href="' + esc(guideHref(F.GUIDE[c.guide] && F.GUIDE[c.guide].id)) + '">' + esc(F.GUIDE[c.guide] ? F.GUIDE[c.guide].label : 'Refill and Maintenance Guide') + '</a>');
        if (c.seal) parts.push('<button type="button" class="sf-act" data-sf-goseal>Find the right seal</button>');
        if (d.type === 'trigger' && (c.part === 'kit' || c.part === 'trigger')) parts.push('<a class="sf-link" href="' + esc(triggersUrl) + '">Trigger sprayers</a>');
        else if (c.part === 'kit' || c.part === 'valve' || c.part === 'pump' || c.part === 'trigger') parts.push('<a class="sf-link" href="' + esc(partsUrl) + '">Parts and seal kits</a>');
        return '<li class="sf-cause" id="sf-c-' + esc(c.id) + '"><div class="sf-cause__top"><span class="sf-cause__n" aria-hidden="true">' + c.rank + '</span><div>' +
          '<p class="sf-cause__h"' + (c.rank === 1 ? ' id="sf-answer" tabindex="-1"' : '') + '><span class="sf-sr">' + (c.rank === 1 ? 'Most likely: ' : 'Then: ') + '</span>' + esc(c.title) + '</p>' +
          (c.why.length ? '<p class="sf-cause__why">' + esc(c.why.map(function (l) { return l.text; }).join(' ')) + '</p>' : '') +
          '</div></div>' +
          (c.checks.length ? '<p class="sf-cause__k">Check</p><ul>' + c.checks.map(function (l) { return '<li>' + esc(l.text) + '</li>'; }).join('') + '</ul>' : '') +
          (c.fix.length ? '<p class="sf-cause__k">Fix</p><ul>' + c.fix.map(function (l) { return '<li>' + esc(l.text) + '</li>'; }).join('') + '</ul>' : '') +
          (parts.length ? '<div class="sf-cause__more">' + parts.join('') + '</div>' : '') +
          srcLine(c.src) + '</li>';
      }).join('') + '</ol>' +
      (d.note.length ? d.note.map(function (l) { return '<p class="sf-note">' + esc(l.text) + '</p>'; }).join('') : '') +
      '<div class="sf-actions" id="sf-fix-acts"><button type="button" class="sf-act" data-sf-copy>Copy the steps</button>' +
      '<button type="button" class="sf-act" data-sf-link>Copy link</button><button type="button" class="sf-act" data-sf-print>Print</button></div>' +
      '<p class="sf-msg" id="sf-fix-msg" role="status"></p>';
    out.innerHTML = html;
  }

  // ---------------------------------------------------------------- seal finder

  // Everyday names first when several match equally (as the Dilution Calculator).
  var COMMON = ['bleach', 'hydrochloric-acid', 'phosphoric-acid', 'citric-acid', 'acetic-acid', 'sulphuric-acid', 'sodium-hydroxide',
    'isopropyl-alcohol', 'ethanol', 'diesel', 'plant-protection', 'acid-wash', 'water-based-cleaner', 'solvent-degreaser', 'brake-cleaner'];
  function rank(row) { var i = COMMON.indexOf(row.id); return i < 0 ? 99 : i; }
  function closeList() { var box = $('#sf-chem-sug'); if (box) box.innerHTML = ''; }

  function suggest() {
    var q = val('sf-chem');
    var box = $('#sf-chem-sug');
    if (state.chem && q === state.chem.name) { closeList(); return; }
    // A changed box clears the old answer, and the address with it, so a copied link never shares it (SF-05).
    if (state.chem) { state.chem = null; renderStrengthRow(); renderSeal(); saveLink(); }
    if (q.trim().length < 2) { closeList(); return; }
    load().then(function (d) {
      if (val('sf-chem') !== q) return;
      if (!d) {
        box.innerHTML = '<p class="sf-sug__count">We could not get the chemical list just now. <button type="button" class="sf-act" data-sf-retry>Try again</button></p>';
        return;
      }
      var res = E.lookup(d, q, 40);
      // A number after the name with no % sign ("sulphuric acid 50", "water based cleaner 9") is its strength or pH,
      // as "50%" is (SF-05).
      var tail = /^(.*[^\d\s.,%])\s+(\d+(?:[.,]\d+)?)\s*$/.exec(q);
      if (tail && res.parsed.strength === null && res.parsed.ph === null) {
        var res2 = E.lookup(d, tail[1], 40);
        if (res2.matches.length && (!res.matches.length || (E.isClear(res2) && res2.matches[0].score >= 100))) {
          res = res2;
          res.parsed.strength = parseFloat(tail[2].replace(',', '.'));
        }
      }
      var order = function (a, b) { return b.score - a.score || rank(a.row) - rank(b.row); };
      res.matches.sort(order);
      if (res.parsed.strength !== null || res.parsed.ph !== null) state.pendingStrength = res.parsed.ph !== null ? res.parsed.ph : res.parsed.strength;
      else state.pendingStrength = null;
      var list = res.matches.length ? res.matches : res.suggestions;
      if (res.matches.length && E.isClear(res) && res.matches[0].score >= 100) {
        // Shown straight away, as the guide does, but the box keeps what was typed, so the next key is not lost
        // (SF-02), and the other chemicals the words match stay one tap away (SF-03).
        pick(res.matches[0].row.id, 'auto');
        var others = res.matches.slice(1, 5);
        box.innerHTML = others.length ? '<p class="sf-sug__count">' + esc(F.TEXT.otherMatches) + '</p>' + others.map(sugBtn).join('') : '';
        return;
      }
      if (!list.length) {
        box.innerHTML = '<p class="sf-sug__count" role="status">' + esc(F.TEXT.noMatch) + ' <a class="sf-link" href="' + esc(F.quoteUrl(quoteBase, { note: F.TEXT.noMatchNote })) + '" data-sf-ask>Ask us about it</a></p>';
        return;
      }
      var shown = list.slice(0, 6);
      box.innerHTML = '<p class="sf-sug__count">' + (res.matches.length ? (shown.length === 1 ? '1 match. Tap it to check it.' : shown.length + ' matches. Tap the one you mean.') : 'Did you mean:') + '</p>' +
        shown.map(sugBtn).join('');
    });
  }
  function sugBtn(x) { return '<button type="button" class="sf-sug__btn" data-sf-chem="' + esc(x.row.id) + '">' + esc(x.row.name) + '</button>'; }

  // how: 'auto' while typing (the box keeps the visitor's words), 'list' when tapped in the list.
  function pick(id, how) {
    return load().then(function (d) {
      if (!d) return;
      var row = E.byId(d, id);
      if (!row) return;
      // The strength box belongs to the chemical it was typed for; a different chemical starts empty (as DIL-01).
      var before = state.strengthFor;
      state.typed = how ? val('sf-chem').trim().slice(0, 40) : (state.typed || '');
      state.chem = row;
      if (how !== 'auto') document.getElementById('sf-chem').value = row.name;
      // A strength typed with the name ("bleach 12.5%") is used; a different chemical starts with an empty strength.
      if (state.pendingStrength !== null && state.pendingStrength !== undefined && row.scale) document.getElementById('sf-strength').value = String(state.pendingStrength);
      else if (before && before !== row.id) document.getElementById('sf-strength').value = '';
      state.pendingStrength = null;
      state.strengthFor = row.id;
      if (how !== 'auto') closeList();
      renderStrengthRow();
      firstUse('seal');
      state.focus = 'seal';
      renderSeal();
      saveLink();
    });
  }

  function renderStrengthRow() {
    var rowEl = $('#sf-strength-row');
    var row = state.chem;
    if (!row || !row.scale) { rowEl.hidden = true; return; }
    rowEl.hidden = false;
    var ph = row.scale === 'pH';
    $('#sf-strength-label').innerHTML = (ph ? 'pH of the product' : esc(row.scaleLabel || 'Strength (%)')) + ' <span class="sf-opt">(' + (row.cap ? 'optional' : 'from the label or SDS') + ')</span>';
    $('#sf-strength-hint').textContent = ph ? F.TEXT.phHint : (row.max ? 'Rated ' + row.max + '. ' : '') + F.TEXT.pctHint;
  }

  function readStrength() {
    var row = state.chem;
    var err = $('#sf-strength-err');
    var input = document.getElementById('sf-strength');
    if (!row || !row.scale) { err.hidden = true; input.removeAttribute('aria-invalid'); return null; }
    var r = F.readStrength(val('sf-strength'), row.scale);
    if (r.error) {
      err.hidden = false; err.textContent = r.error; input.setAttribute('aria-invalid', 'true'); input.setAttribute('aria-describedby', 'sf-strength-hint sf-strength-err');
    } else {
      err.hidden = true; input.removeAttribute('aria-invalid'); input.setAttribute('aria-describedby', 'sf-strength-hint');
    }
    return r.value;
  }

  function badge(rating) { return '<span class="sf-badge sf-badge--' + esc(rating) + '">' + esc(F.RATING[rating] || rating) + '</span>'; }

  function renderSeal() {
    var out = $('#sf-seal-out');
    if (!state.chem || !state.data) { state.ans = null; out.innerHTML = ''; updateQuote(); peek(); return; }
    var strength = readStrength();
    var oilsEl = $('input[name="sf-oils"]:checked');
    state.oils = oilsEl ? oilsEl.value : '';
    state.hot = !!(document.getElementById('sf-hot') || {}).checked;
    var a = F.seal(E, state.data, state.chem, { strength: strength, oils: state.oils, hot: state.hot });
    state.ans = a;
    // A strength the box cannot read is left out, and the answer says so rather than looking final (SF-18).
    var strengthBad = !!(state.chem.scale && F.readStrength(val('sf-strength'), state.chem.scale).error);
    var head = a.groups.length ? a.groups.map(function (g) { return g === 'VITON' ? 'VITON (FKM)' : g; }).join(' or ') + ' seals' : 'Ask us';
    var what;
    if (a.text) what = a.text;
    else if (a.famLabels.length) what = 'The ' + a.famLabels.join(' or the ').toLowerCase() + '.';
    else what = a.note || F.TEXT.noRating;
    var typed = state.typed && state.typed.toLowerCase() !== a.name.toLowerCase() ? ' <span class="sf-opt">(you typed “' + esc(state.typed) + '”)</span>' : '';
    var srcs = a.src.map(function (id) { var s = state.data.sources[id]; return s ? (s.url ? '<a href="' + esc(s.url) + '" rel="noopener">' + esc(s.short) + '</a>' : esc(s.short)) : ''; }).filter(Boolean);
    var strengthLine = '';
    if (a.strength !== null && a.strength !== undefined) strengthLine = '<p class="sf-small">Checked at ' + esc(F.fmtStrength(a.strength, a.scale)) + '.</p>';
    else if (a.needsStrength) strengthLine = '<p class="sf-small">' + esc(F.TEXT.needStrength) + '</p>';
    var scale = a.strength !== null && a.strength >= 1 ? 100 : 10000;
    var guideLink = guideUrl + '#guide&c=' + encodeURIComponent(a.id + (a.strength !== null && a.strength !== undefined && a.scale === 'pct' ? ':' + (Math.round(a.strength * scale) / scale) : '')) + '&m=one' + (a.hot ? '&hot=1' : '');
    out.innerHTML = '<div class="sf-out" id="sf-seal-box">' +
      '<div class="sf-out__top"><p class="sf-out__seal" id="sf-seal-answer" tabindex="-1">' + esc(head) + '</p>' + (strengthBad ? '<span class="sf-badge sf-badge--off">Strength left out</span>' : badge(a.rating)) + '</div>' +
      (strengthBad ? '<p class="sf-small"><b>' + esc(F.TEXT.badStrengthLeft) + '</b></p>' : '') +
      '<p class="sf-small"><b>Checked as:</b> ' + esc(a.name) + typed + '</p>' +
      '<p>' + esc(what) + '</p>' +
      // With oils or solvents the guide's own note for the chemical alone would read as a rating, so it is left out.
      (a.note && a.note !== what && !a.mixed ? '<p class="sf-small">' + esc(a.note) + '</p>' : '') +
      (a.spec && !a.famLabels.length ? '<p class="sf-small"><b>Specialist model:</b> ' + esc(a.spec) + '</p>' : '') +
      strengthLine +
      (a.caveats.length ? '<ul>' + a.caveats.map(function (c) { return '<li>' + esc(c) + '</li>'; }).join('') + '</ul>' : '') +
      '<p class="sf-small">' + esc(F.TEXT.sealCaveat) + '</p>' +
      (srcs.length ? '<p class="sf-small">Sources: ' + srcs.join('; ') + '.</p>' : '') +
      '<p class="sf-small"><a class="sf-link" href="' + esc(guideLink) + '">See the full answer in the Chemical Compatibility Guide</a></p>' +
      buyBlock(a) + '</div>';
    updateQuote();
    loadParts(a);
    scheduleSeal(a);
    peek();
  }

  // What to buy: the sprayers the guide rates for this answer, and the parts we stock with the same seal, read live.
  function buyBlock(a) {
    if (!a.groups.length) {
      return '<div class="sf-buy"><p class="sf-buy__h">What to buy</p><p>' + esc(F.TEXT.buyAsk) + '</p>' +
        '<div class="sf-actions"><a class="sf-btn sf-btn--red" data-sf-quote href="' + esc(quoteHref()) + '">Ask us with your SDS</a>' + sealActs() + '</div>' + sealMsg() + '</div>';
    }
    var rated = [];
    var other = [];
    E.modelsFor(state.data, a.fams).forEach(function (g) {
      g.rated.forEach(function (m) { rated.push('<a href="/products/' + esc(m.handle) + '">Klager ' + esc(m.name) + '</a>'); });
      g.other.forEach(function (m) { other.push('<a href="/products/' + esc(m.handle) + '">' + esc(m.brand + ' ' + m.name) + '</a>'); });
    });
    // The guide's ratings are Klager's; other brands share the seal but not the build (the guide's own note).
    return '<div class="sf-buy"><p class="sf-buy__h">What to buy</p>' +
      (rated.length ? '<p class="sf-small"><b>Sprayers the guide rates for it:</b> ' + rated.join(', ') + '</p>' : '') +
      (other.length ? '<p class="sf-small"><b>Same seal, check the data sheet:</b> ' + other.join(', ') + '</p>' : '') +
      '<p class="sf-small"><b>' + esc(a.groups.join(' and ')) + ' parts we stock' + (state.type ? ' for a ' + esc(F.type(state.type).name.toLowerCase()) : '') + ':</b></p>' +
      '<div id="sf-parts" aria-live="polite"><p class="sf-small">Checking our parts list…</p></div>' +
      '<p class="sf-small">' + esc(F.TEXT.fitsRule) + ' <a class="sf-link" href="' + esc(partsUrl) + '">All parts and seal kits</a></p>' +
      '<div class="sf-actions"><a class="sf-btn sf-btn--red" data-sf-quote href="' + esc(quoteHref()) + '">Request a quote</a>' + sealActs() + '</div>' + sealMsg() + '</div>';
  }
  // With no steps on screen, the seal answer has its own Copy link and Print (SF-15).
  function sealActs() { return state.diag ? '' : '<button type="button" class="sf-act" data-sf-link data-sf-msg="sf-seal-msg">Copy link</button><button type="button" class="sf-act" data-sf-print>Print</button>'; }
  function sealMsg() { return state.diag ? '' : '<p class="sf-msg" id="sf-seal-msg" role="status"></p>'; }

  function fetchPart(handle) {
    if (state.parts[handle]) return state.parts[handle];
    state.parts[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('part ' + r.status); return r.json(); })
      .then(function (p) { return F.readProduct(p); })
      .catch(function () { delete state.parts[handle]; return null; });
    return state.parts[handle];
  }

  function loadParts(a) {
    var box = document.getElementById('sf-parts');
    if (!box || !a.groups.length) return;
    var cands = F.kitCandidates(a.groups, state.type);
    var key = a.groups.join('+') + '|' + (state.type || '');
    state.partsFor = key;
    if (!cands.length || !window.fetch) {
      box.innerHTML = '<p class="sf-small">' + esc(F.TEXT.noKit.replace('{type}', state.type ? F.type(state.type).name.toLowerCase() : 'sprayer')) + '</p>';
      return;
    }
    Promise.all(cands.map(function (k) { return fetchPart(k.handle).then(function (p) { return { k: k, p: p }; }); })).then(function (list) {
      if (state.partsFor !== key) return;
      var box2 = document.getElementById('sf-parts');
      if (!box2) return;
      var failed = list.filter(function (x) { return !x.p; }).length;
      var shown = list.filter(function (x) { return x.p && x.p.available && F.saysSeal(x.p, x.k.seal); });
      if (!shown.length) {
        box2.innerHTML = '<p class="sf-small">' + esc(failed === list.length ? F.TEXT.partsFailed : F.TEXT.noKit.replace('{type}', state.type ? F.type(state.type).name.toLowerCase() : 'sprayer')) + '</p>';
        return;
      }
      // Seal kits come first in the list; a long list shows five, and the rest one tap away.
      var FIRST = 5;
      box2.innerHTML = '<ul class="sf-parts">' + shown.map(function (x, i) {
        return '<li class="sf-part"' + (i >= FIRST ? ' data-sf-more hidden' : '') + '><a href="' + esc(x.p.url) + '">' + esc(x.p.title) + '</a>' +
          '<span>Seal: ' + esc(x.p.seal) + '. ' + (x.p.fits ? 'Fits, as our product page says: ' + esc(x.p.fits) + '.' : esc(F.TEXT.noFits)) + '</span></li>';
      }).join('') + '</ul>' +
        (shown.length > FIRST ? '<button type="button" class="sf-act" data-sf-show-parts aria-expanded="false">Show ' + (shown.length - FIRST) + ' more parts</button>' : '');
    });
  }

  // ---------------------------------------------------------------- quote

  function quoteHref() { return F.quoteUrl(quoteBase, F.quote(state.diag, state.ans)); }
  function updateQuote() { $$('[data-sf-quote]').forEach(function (q) { q.setAttribute('href', quoteHref()); }); }

  // ---------------------------------------------------------------- answer bar

  // Shown whenever the tool is on screen but its answer is not (under the site header, or below the fold).
  // Phones and tablets get a bar along the bottom; desktop gets a small box in the corner.
  // Where the site header ends right now: 0 once it has slid away on scroll (ALL-R2). The height alone counted a
  // hidden header, so an answer at the top of the screen was taken as covered.
  function headerBottom() {
    var b = 0;
    try {
      document.querySelectorAll('.shopify-section-header-sticky, sticky-header, .section-header').forEach(function (el) {
        var cs = getComputedStyle(el);
        if (cs.position === 'sticky' || cs.position === 'fixed') b = Math.max(b, el.getBoundingClientRect().bottom);
      });
    } catch (e) { b = state.headerH || 0; }
    return Math.max(0, Math.min(b, 160));
  }
  function answerEl() {
    if (state.focus === 'seal' && state.ans) return document.getElementById('sf-seal-answer');
    if (state.diag) return document.getElementById('sf-answer');
    if (state.ans) return document.getElementById('sf-seal-answer');
    return null;
  }
  // The answer is the whole answer block (the causes, or the seal answer and its parts), not just its first line: while
  // the visitor reads cause 2 or the parts list, the bar would only cover what they are reading.
  // It starts at the answer line itself, so the safety box above the causes does not count as the answer.
  function answerBlock() {
    if (state.focus === 'seal') return state.ans ? document.getElementById('sf-seal-box') : null;
    return state.diag ? document.getElementById('sf-fix-out') : null;
  }
  function answerInView() {
    var a = answerBlock();
    var first = answerEl();
    if (!a || !first) return true;
    return blockInView(first, a);
  }
  // The other answer on screen: reading the causes while the bar points at the seal answer, or the other way round,
  // the bar would sit on what is being read, so it stays away then too (SF-09).
  function otherInView() {
    if (state.focus === 'seal') {
      var line = document.getElementById('sf-answer');
      var box = document.getElementById('sf-fix-out');
      return !!(state.diag && line && box && blockInView(line, box));
    }
    var sl = document.getElementById('sf-seal-answer');
    var sb = document.getElementById('sf-seal-box');
    return !!(state.ans && sl && sb && blockInView(sl, sb));
  }
  function blockInView(first, a) {
    var box = a.getBoundingClientRect();
    var r = { top: first.getBoundingClientRect().top, bottom: box.bottom, height: box.bottom - first.getBoundingClientRect().top };
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var wide = window.matchMedia && window.matchMedia('(min-width: 990px)').matches;
    // Counted as on screen only when a good part of it shows between the header and the bar: an answer cut off at the
    // top, under a header that is there or has slid away, is not (ALL-R2). The phone bar covers the bottom 76 px.
    var hb = headerBottom();
    var floor = vh - (wide ? 24 : 76);
    var line = first.getBoundingClientRect();
    if (line.top >= hb - 1 && line.bottom <= floor) return true;
    var shown = Math.min(r.bottom, floor) - Math.max(r.top, hb);
    return shown >= Math.min(120, r.height / 2);
  }
  function toolInView() {
    var r = root.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return r.bottom > 80 && r.top < vh - 80;
  }
  // The bar or corner box would sit on the answer's buttons (Copy, Copy link, Print, Request a quote): it stays
  // hidden until they move clear.
  function coversActions(bar) {
    var acts = $$('#sf-fix-acts, #sf-seal-box .sf-actions');
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var vw = document.documentElement.clientWidth || window.innerWidth;
    var cs = getComputedStyle(bar);
    var bottom = parseFloat(cs.bottom) || 0;
    var right = parseFloat(cs.right) || 0;
    var top = vh - bottom - bar.offsetHeight;
    var left = vw - right - bar.offsetWidth;
    return acts.some(function (a) { var r = a.getBoundingClientRect(); return r.height > 0 && r.bottom > top && r.top < vh - bottom && r.right > left; });
  }
  // Typing on a short screen (the phone keyboard is up): the bar would cover the box.
  function typingOnShortScreen() {
    var a = document.activeElement;
    if (!a || !root.contains(a) || !/^(INPUT|SELECT|TEXTAREA)$/.test(a.tagName) || /^(radio|checkbox)$/.test(a.type)) return false;
    var h = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    return h < 450;
  }
  function peekText() {
    if (state.focus === 'seal' && !state.ans) return '';
    if (state.focus === 'seal' && state.ans) {
      var a = state.ans;
      return 'Seal: <b>' + esc(a.groups.length ? a.groups.join(' or ') : 'Ask us') + '</b>' + (a.groups.length ? ' <span>' + esc(F.RATING[a.rating]) + '</span>' : '') + '<span class="sf-sr">. Show the answer.</span>';
    }
    if (state.diag) return 'Most likely: <b>' + esc(state.diag.causes[0].title) + '</b><span class="sf-sr">. Show the steps.</span>';
    return '';
  }
  function peek() {
    var bar = document.getElementById('sf-peek');
    if (!bar) return;
    var txt = peekText();
    var show = !!txt && toolInView() && !answerInView() && !otherInView() && !typingOnShortScreen();
    if (txt) setHtml(document.getElementById('sf-peek-txt'), txt);
    if (show && coversActions(bar)) show = false;
    bar.setAttribute('data-show', show ? '1' : '0');
    bar.tabIndex = show ? 0 : -1;
    var wide = window.matchMedia && window.matchMedia('(min-width: 990px)').matches;
    try { document.documentElement.style.scrollPaddingBottom = show && !wide ? '84px' : ''; } catch (e) { /* ignore */ }
  }
  function watchPeek() {
    var bar = document.getElementById('sf-peek');
    if (!bar) return;
    bar.hidden = false;
    var ticking = false;
    var onMove = function () {
      if (ticking) return;
      ticking = true;
      (window.requestAnimationFrame || setTimeout)(function () { ticking = false; peek(); });
    };
    window.addEventListener('scroll', onMove, { passive: true });
    window.addEventListener('resize', onMove);
    // The page settles after load and once the fonts arrive, so the bar is checked again then (ALL-R2).
    window.addEventListener('load', onMove);
    try { if (document.fonts && document.fonts.ready) document.fonts.ready.then(onMove); } catch (e) { /* ignore */ }
    peek();
  }
  function headerHeight() {
    try {
      var h = 0;
      document.querySelectorAll('.shopify-section-header-sticky, sticky-header, .section-header').forEach(function (el) {
        var cs = getComputedStyle(el);
        if (cs.position === 'sticky' || cs.position === 'fixed') h = Math.max(h, el.getBoundingClientRect().height);
      });
      state.headerH = Math.round(Math.min(h, 160));
    } catch (e) { /* keep the last one */ }
  }
  // Scrolls an element to just under the site header, which slides back in on the way up.
  // mustShow: an element that has to end up on screen, clear of the answer bar (the answer, below the safety box).
  function goTo(el, focusEl, mustShow, instant) {
    if (!el) return;
    headerHeight();
    var smooth = !instant && !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    var top = el.getBoundingClientRect().top + window.pageYOffset - (state.headerH || 0) - 16;
    if (mustShow) {
      var r = mustShow.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      top = Math.max(top, r.bottom + window.pageYOffset - (vh - 110));
    }
    try { window.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' }); } catch (err) { window.scrollTo(0, Math.max(0, top)); }
    try { (focusEl || el).focus({ preventScroll: true }); } catch (err) { /* ignore */ }
  }

  // ---------------------------------------------------------------- counting results, the link, copy and print

  function scheduleFix() {
    clearTimeout(state.timer);
    state.timer = setTimeout(function () {
      var d = state.diag;
      if (!d) return;
      var sig = d.type + '_' + d.symptom;
      if (sig === state.lastSig) return;
      state.lastSig = sig;
      track('guide_result_shown', { guide_kind: 'troubleshoot', guide_via: sig.replace(/-/g, '_'), guide_list_size: d.causes.length });
      var s = document.getElementById('sf-status');
      if (s) s.textContent = 'Most likely: ' + d.causes[0].title + '. ' + d.causes.length + ' causes to check, in order.';
    }, 800);
  }
  function scheduleSeal(a) {
    clearTimeout(state.sealTimer);
    state.sealTimer = setTimeout(function () {
      // Counted once per answer: tapping an oils answer that changes nothing is not a new result (SF-21).
      var sig = [a.id, a.strength, a.rating, a.groups.join('+'), a.hot, a.mixed].join('|');
      if (sig === state.lastSealSig) return;
      state.lastSealSig = sig;
      track('guide_result_shown', {
        guide_kind: 'seal', guide_chemical: a.id, guide_rating: a.rating, guide_strength_given: a.strength !== null && a.strength !== undefined ? 1 : 0,
        guide_ask_us: a.rating === 'ask' ? 1 : 0, guide_mixed_seals: a.groups.length > 1 ? 1 : 0, guide_via: state.fromFix ? 'troubleshooter' : undefined,
      });
      var s = document.getElementById('sf-status');
      if (s) s.textContent = 'Seal finder: ' + (a.groups.length ? a.groups.join(' or ') + ', ' + F.RATING[a.rating] : 'ask us') + ' for ' + a.name + '.';
    }, 1000);
  }

  function linkState() {
    var a = state.ans;
    return { type: state.type, symptom: state.symptom, chem: state.chem ? state.chem.id : null, strength: a ? a.strength : null, oils: state.oils, hot: state.hot };
  }
  function shareUrl() { return location.origin + location.pathname + F.encode(linkState()); }
  // The answer goes into the address as it changes, so a reload, Back or a phone that reloads the tab brings it back.
  function saveLink() {
    if (!state.edited) return;
    clearTimeout(state.linkTimer);
    state.linkTimer = setTimeout(function () {
      try { history.replaceState(null, '', location.pathname + location.search + F.encode(linkState())); } catch (e) { /* ignore */ }
    }, 400);
  }

  function copyText(text, msg, where) {
    var flag = document.getElementById(where || 'sf-fix-msg');
    var done = function () { if (flag) { flag.textContent = msg; setTimeout(function () { if (flag.textContent === msg) flag.textContent = ''; }, 2500); } };
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(text).then(done, function () { fallback(text); done(); }); return; }
    } catch (e) { /* use the fallback */ }
    fallback(text);
    done();
  }
  function fallback(text) {
    var t = document.createElement('textarea');
    t.value = text; t.setAttribute('readonly', ''); t.style.position = 'fixed'; t.style.opacity = '0';
    document.body.appendChild(t); t.select();
    try { document.execCommand('copy'); } catch (e) { /* ignore */ }
    document.body.removeChild(t);
  }

  // The steps and the seal answer on A4, for the workshop wall or the job folder.
  function printCard() {
    var d = state.diag;
    var a = state.ans;
    var box = document.getElementById('sf-print');
    if (!box || (!d && !a)) return;
    var h = '<h1>' + esc(d ? d.typeName + ': ' + d.symptomName : 'Seal finder') + '</h1>';
    if (d) {
      h += '<div class="sf-p-box"><b>Before you start.</b> ' + esc(d.safety.map(function (l) { return l.text; }).join(' ')) + '</div>';
      d.causes.forEach(function (c) {
        h += '<div class="sf-p-cause"><h3>' + c.rank + '. ' + esc(c.title) + '</h3>' +
          (c.checks.length ? '<p><b>Check</b></p><ul>' + c.checks.map(function (l) { return '<li>' + esc(F.offPage(l.text)) + '</li>'; }).join('') + '</ul>' : '') +
          (c.fix.length ? '<p><b>Fix</b></p><ul>' + c.fix.map(function (l) { return '<li>' + esc(F.offPage(l.text)) + '</li>'; }).join('') + '</ul>' : '') + '</div>';
      });
    }
    if (a) {
      h += '<h2>Seal for ' + esc(a.name) + (a.strength !== null && a.strength !== undefined ? ' at ' + esc(F.fmtStrength(a.strength, a.scale)) : '') + '</h2>' +
        '<p><b>' + esc(a.groups.length ? a.groups.join(' or ') : 'Ask us') + '</b> (' + esc(F.RATING[a.rating]) + '). ' + esc(a.text || (a.famLabels.length ? 'The ' + a.famLabels.join(' or the ').toLowerCase() + '.' : a.note || '')) + '</p>' +
        a.caveats.map(function (c) { return '<p>' + esc(c) + '</p>'; }).join('');
    }
    h += '<p class="sf-p-foot">' + esc(F.TEXT.printFoot) + ' ' + esc(shareUrl()) + '</p>';
    // Straight under the body, so everything else on the page can be left out of the print (as the chemical register).
    if (box.parentNode !== document.body) document.body.appendChild(box);
    box.innerHTML = h;
    var html = document.documentElement;
    html.classList.add('nora-print-sf');
    var done = function () { html.classList.remove('nora-print-sf'); box.innerHTML = ''; window.removeEventListener('afterprint', done); };
    window.addEventListener('afterprint', done);
    try { window.print(); } catch (e) { /* ignore */ }
    setTimeout(function () { if (!window.matchMedia || !window.matchMedia('print').matches) done(); }, 1000);
  }

  // ---------------------------------------------------------------- events

  root.addEventListener('change', function (e) {
    var t = e.target;
    if (t.name === 'sf-type') {
      state.type = t.value;
      if (state.symptom && !F.diagnose(state.type, state.symptom)) state.symptom = null;
      firstUse('troubleshoot');
      state.focus = 'fix';
      renderSymptoms();
      renderFix();
      if (state.ans) renderSeal();
      updateQuote();
      scheduleFix();
      saveLink();
      peek();
      return;
    }
    if (t.name === 'sf-sym') {
      state.symptom = t.value;
      firstUse('troubleshoot');
      state.focus = 'fix';
      renderFix();
      if (state.ans) renderSeal();
      updateQuote();
      scheduleFix();
      saveLink();
      peek();
      return;
    }
    if (t.name === 'sf-oils' || t.id === 'sf-hot') {
      firstUse('seal');
      state.focus = 'seal';
      renderSeal();
      saveLink();
    }
  });
  root.addEventListener('input', function (e) {
    if (e.target.id === 'sf-chem') { suggest(); return; }
    if (e.target.id === 'sf-strength') { firstUse('seal'); state.focus = 'seal'; renderSeal(); saveLink(); }
  });
  root.addEventListener('focusin', function (e) {
    // The dataset starts loading as soon as the chemical box is used, so the first match is quick.
    if (e.target.id === 'sf-chem') load();
    peek();
  });
  root.addEventListener('focusout', function () { setTimeout(peek, 0); });
  if (window.visualViewport) window.visualViewport.addEventListener('resize', function () { peek(); });
  root.addEventListener('keydown', function (e) {
    // Enter in the chemical box picks the only match, never submits the form.
    if (e.key === 'Enter' && e.target.id === 'sf-chem') {
      e.preventDefault();
      var first = $('#sf-chem-sug [data-sf-chem]');
      if (first && $$('#sf-chem-sug [data-sf-chem]').length === 1) first.click();
    }
  });
  var form = document.getElementById('sf-form');
  if (form) form.addEventListener('submit', function (e) { e.preventDefault(); });

  root.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target.closest('button, a') : null;
    if (!t || !root.contains(t)) return;
    // The tapped button leaves the page with the list, so focus goes to the answer (SF-11).
    if (t.hasAttribute('data-sf-chem')) {
      pick(t.getAttribute('data-sf-chem'), 'list').then(function () {
        var ans = document.getElementById('sf-seal-answer');
        if (ans) try { ans.focus({ preventScroll: true }); } catch (err) { /* ignore */ }
      });
      return;
    }
    if (t.hasAttribute('data-sf-retry')) { state.loading = null; suggest(); return; }
    if (t.hasAttribute('data-sf-show-parts')) {
      $$('#sf-parts [data-sf-more]').forEach(function (li) { li.hidden = false; });
      t.hidden = true;
      var firstMore = $('#sf-parts [data-sf-more] a');
      if (firstMore) firstMore.focus();
      return;
    }
    if (t.hasAttribute('data-sf-goseal')) {
      state.fromFix = true;
      state.focus = 'seal';
      peek();
      track('guide_tool_use', { guide_kind: 'seal', guide_via: 'troubleshooter' });
      goTo(document.getElementById('sf-seal'), document.getElementById('sf-chem'));
      return;
    }
    if (t.id === 'sf-peek') {
      var target = state.focus === 'seal' && state.ans ? document.getElementById('sf-seal-box') : document.getElementById('sf-step-fix');
      goTo(target, answerEl(), answerEl());
      return;
    }
    if (t.hasAttribute('data-sf-copy')) {
      copyText(F.text(state.diag, state.ans, shareUrl()), 'Copied. Paste it into a message or a job note.');
      track('guide_share', { guide_kind: 'troubleshoot', guide_via: 'copy_result' });
      return;
    }
    if (t.hasAttribute('data-sf-link')) {
      copyText(shareUrl(), 'Link copied.', t.getAttribute('data-sf-msg'));
      track('guide_share', { guide_kind: state.diag ? 'troubleshoot' : 'seal', guide_via: 'copy_link' });
      return;
    }
    if (t.hasAttribute('data-sf-print')) { track('guide_print', { guide_kind: state.diag ? 'troubleshoot' : 'seal' }); printCard(); return; }
    if (t.hasAttribute('data-sf-quote') || t.hasAttribute('data-sf-ask')) {
      track('guide_quote_click', { guide_kind: state.ans ? 'seal' : 'troubleshoot', guide_chemical: state.ans ? state.ans.id : undefined, guide_rating: state.ans ? state.ans.rating : undefined });
    }
  });

  // ---------------------------------------------------------------- start

  var nav = '';
  try { nav = (performance.getEntriesByType('navigation')[0] || {}).type || ''; } catch (e) { /* ignore */ }
  // A link opened fresh scrolls to its answer, clear of the site header, as the guide's links do (SF-10). A reload or
  // Back keeps the browser's own scroll position.
  var jumpOnRestore = nav !== 'reload' && nav !== 'back_forward';
  function jumpToAnswer() {
    if (state.diag) goTo(document.getElementById('sf-step-fix'), document.getElementById('sf-answer'), null, true);
    else if (state.ans) goTo(document.getElementById('sf-seal'), document.getElementById('sf-seal-answer'), document.getElementById('sf-seal-answer'), true);
  }

  function restore() {
    var s = F.decode(location.hash);
    if (!s) return false;
    state.type = s.type;
    state.symptom = s.symptom;
    state.oils = s.oils;
    state.hot = s.hot;
    $$('input[name="sf-oils"]').forEach(function (r) { r.checked = r.value === s.oils; });
    var hot = document.getElementById('sf-hot');
    if (hot) hot.checked = !!s.hot;
    if (s.chem) {
      state.focus = 'seal';
      load().then(function (d) {
        var row = d ? E.byId(d, s.chem) : null;
        if (!row) { renderSeal(); return; }
        state.chem = row;
        document.getElementById('sf-chem').value = row.name;
        if (s.strength !== null && row.scale) document.getElementById('sf-strength').value = String(s.strength);
        renderStrengthRow();
        renderSeal();
        if (jumpOnRestore && !state.diag) jumpToAnswer();
      });
    }
    return !!(s.type || s.chem);
  }

  var restored = restore();
  renderTypes();
  renderSymptoms();
  renderFix();
  updateQuote();
  // A share link opened in a tab already on this page changes only the part after #, so the browser does not load the
  // page again and the old answer stayed. Load it again, so the new link is read like any other (ALL-R3). The tool
  // writes its own link with replaceState, which does not fire hashchange.
  window.addEventListener('hashchange', function () { if (location.hash.indexOf('#fix&') === 0) location.reload(); });
  watchPeek();
  root.setAttribute('data-ready', '1');
  if (restored && jumpOnRestore && state.diag) setTimeout(jumpToAnswer, 0);
  // The chemical list loads once the page is idle, so the seal finder still works if the signal drops later (SF-16).
  var idle = window.requestIdleCallback || function (f) { return setTimeout(f, 2500); };
  if (document.readyState === 'complete') idle(function () { load(); });
  else window.addEventListener('load', function () { idle(function () { load(); }); });
  if (restored) {
    state.used = true;
    state.edited = true;
    if (nav !== 'reload' && nav !== 'back_forward' && state.diag) {
      state.lastSig = state.diag.type + '_' + state.diag.symptom;
      track('guide_result_shown', { guide_kind: 'troubleshoot', guide_via: state.lastSig.replace(/-/g, '_'), guide_list_size: state.diag.causes.length, guide_restored: 1 });
    }
  }
})();
